/**
 * Main app logic — ESM module.
 *
 * История:
 *  - Изначально inline <script> в index.html (строки 937-10122).
 *  - Phase 2 modularization, Sprint 2 (2026-05-07): извлечён в отдельный файл.
 *  - Phase 4 Vite/ESM (Sprint 6): добавлен `export` для класса
 *    (для будущего split на доменные подмодули в Phase 5).
 *
 * TODO: разбить на модули (calc/render/state/events/...) — Phase 5.
 *
 * Загружается через side-effect import из public/js/entry.js.
 * Класс также экспортирован как named export — для использования
 * в Phase 5 модулях через `import { ProfessionalQuoteCalculator } from './app.js'`.
 */

        // Async replacements for native confirm()/alert() — used in catalog/db imports.
        import { confirmModal } from '/public/js/components/text-prompt-modal.js';

        // i18n helper — returns translation if loaded, else fallback (English).
        // Safe to call before i18n module loads.
        const _t = (k, fb) => {
            const v = window.i18n?.t?.(k);
            return (v && v !== k) ? v : (fb || k);
        };

        // Глобальная защита от ошибок браузерных расширений (LastPass, Grammarly и т.д.)
        window.addEventListener('error', function(event) {
            // Игнорировать ошибки из content_script расширений браузера
            if (event.filename && event.filename.includes('content_script')) {
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
            // Игнорировать ошибки расширений с типичными признаками
            if (event.message && (
                event.message.includes('ControlLooksLikePasswordCredentialField') ||
                event.message.includes('chrome-extension://') ||
                event.message.includes('moz-extension://')
            )) {
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        }, true);

        export class ProfessionalQuoteCalculator {
            constructor() {
                // Version control constants
                this.APP_VERSION = '2.5.0';           // App version
                this.CATALOG_VERSION = '1.2.0';       // Catalog format version
                this.QUOTE_VERSION = '1.1.0';         // Quote format version
                this.DATA_SCHEMA_VERSION = '1.0.0';   // localStorage schema version

                // Configuration object
                this.config = {
                    currency: '$',
                    locale: 'en-US',
                    maxFileSize: 5 * 1024 * 1024, // 5MB
                    debounceDelays: {
                        search: 300,
                        calculations: 150,
                        detailRender: 100,
                        bulkControls: 50,
                        autosave: 8000 // Автосохранение через 8 секунд после изменений (5-10 сек диапазон)
                    },
                    ui: {
                        animationDuration: 300,
                        notificationTimeout: 3000,
                        minTouchTarget: 44 // px
                    },
                    validation: {
                        maxServices: 1000,
                        maxServiceNameLength: 100,
                        minPrice: 0
                    }
                };

                // Timeout IDs for race condition prevention
                this.timeoutIds = {
                    update: null,
                    search: null,
                    detailRender: null,
                    bulkUpdate: null,
                    autosave: null
                };

                // Timeout для сохранения глобальных настроек
                this.globalSettingsSaveTimeout = null;

                // Храним состояние раскрытых секций деталей в чеклисте
                this.expandedChecklistDetails = new Set();

                this.state = {
                    services: [],
                    optionalServices: [], // Опциональные услуги (не входят в основную смету)
                    hotels: [], // Отели (отдельная секция)
                    flights: [], // Перелёты (отдельная секция)
                    otherServices: [], // Прочие услуги - визы, страховки и т.д.
                    paxCount: 27,
                    hiddenMarkup: 0,
                    partnerCommission: 0, // Комиссия партнера (% от себестоимости)
                    prepaymentManual: 0,  // Sprint 19a: ручная сумма предоплаты (доп к авто-расчёту)
                    tourStart: '',
                    tourEnd: '',
                    clientName: '',
                    clientPhone: '',
                    clientEmail: '',
                    quoteDescription: '',
                    programDescription: '', // Описание программы для печати
                    bookingTerms: '', // Условия бронирования для печати
                    activeCategory: 'all',
                    currentServiceType: 'service', // Тип создаваемой услуги: 'service', 'hotel', 'flight', 'other', 'optional'
                    selectedServices: new Set(), // For bulk operations
                    saveFolder: '', // Выбранная папка для сохранения
                    currentQuoteFile: '', // Имя текущего открытого файла
                    currentQuoteId: null, // Уникальный ID текущей сметы (UUID)
                    isQuoteSaved: true, // Флаг сохранения
                    lastSaveFolder: '', // Последняя папка сохранения
                    lastLoadFolder: '', // Последняя папка загрузки
                    servicesSortBy: 'day', // day по умолчанию, order (отсутствует), region, contractor
                    servicesSortDirection: 'asc', // asc, desc
                    // ===== VIEW MODE STATE - Режим отображения услуг =====
                    servicesViewMode: 'cards', // 'cards' - карточки (по умолчанию), 'table' - таблица
                    // ===== CHECKLIST MODE STATE - Режим чеклиста бронирований =====
                    currentMode: 'quote', // 'quote' - смета, 'checklist' - чеклист
                    checklistViewMode: 'day', // 'day' - по дням, 'supplier' - по поставщикам
                    checklistFilter: 'all', // 'all', 'booked', 'paid', 'unprocessed'
                    // ===== PHASE 8a — QUICK FILTER В CARDS MODE =====
                    // Фильтр карточек услуг по статусу бронирования/оплаты внутри основного режима «Смета».
                    // 'all' (по умолчанию), 'not_booked' (не забронировано), 'not_paid' (не оплачено)
                    checklistQuickFilter: 'all',
                    // ===== PHASE 8c — MULTI-CURRENCY =====
                    // Валюта клиента, в которой показываем итоги в правой панели (USD pivot).
                    // 'USD' по умолчанию (backward-compat). При первом загруже org-настроек
                    // _refreshCurrencyCache() подменяет на default_client из org_settings.
                    clientCurrency: 'USD'
                };

                // File System Access API handles
                this.catalogDirectoryHandle = null;

                // Emoji to Lucide icon mapping
                this.iconMap = {
                    // Транспорт
                    '🚗': 'car', '🚐': 'bus', '🚌': 'bus', '✈️': 'plane', '🚁': 'plane',
                    '🚂': 'train', '🚇': 'train', '🚆': 'train', '🚢': 'ship', '⛴️': 'ship',
                    '🚤': 'ship', '🛥️': 'ship', '⛵': 'sailboat', '🚴': 'bike', '🚲': 'bike',
                    // Размещение
                    '🏨': 'hotel', '🏩': 'hotel', '🏠': 'home', '🏡': 'home', '🏘️': 'home',
                    '🏕️': 'tent', '⛺': 'tent', '🏰': 'castle', '🏛️': 'landmark',
                    // Питание
                    '🍽️': 'utensils', '🍕': 'pizza', '🍔': 'burger', '☕': 'coffee',
                    '🍷': 'wine', '🥂': 'wine', '🍰': 'cake', '🍺': 'beer',
                    // Активности и развлечения
                    '🎭': 'drama', '🎪': 'tent', '🎢': 'ferris-wheel', '🎡': 'ferris-wheel',
                    '🎠': 'ferris-wheel', '🎯': 'target', '🎳': 'circle', '🎮': 'gamepad-2',
                    '🎬': 'film', '🎨': 'palette', '🎸': 'music', '🎹': 'music',
                    '⛷️': 'mountain-snow', '🏔️': 'mountain', '🏖️': 'palmtree', '🌊': 'waves',
                    '🌲': 'tree-pine', '⛰️': 'mountain', '🌮': 'utensils', '🏛️': 'landmark',
                    // Услуги
                    '👨‍🏫': 'user', '📷': 'camera', '🎤': 'mic', '💼': 'briefcase',
                    '🛡️': 'shield', '💊': 'pill', '🔧': 'wrench', '📱': 'smartphone',
                    '💳': 'credit-card', '🎫': 'ticket', '📋': 'clipboard', '📄': 'file-text',
                    // Разное
                    '💎': 'gem', '🏆': 'trophy', '📍': 'map-pin', '🗺️': 'map',
                    '⏰': 'clock', '📅': 'calendar', '✅': 'check', '❓': 'circle-question-mark',
                    '💰': 'dollar-sign', '📊': 'bar-chart', '🎁': 'gift', '⭐': 'star'
                };

                // Default categories
                this.categories = [
                    { id: 'undefined', name: 'Не определено', icon: '❓', system: true }, // Системная категория для услуг без категории
                    { id: 'transfer', name: 'Трансфер', icon: '🚗' },
                    { id: 'accommodation', name: 'Отели', icon: '🏨' },
                    { id: 'guide', name: 'Гиды', icon: '👨‍🏫' },
                    { id: 'activity', name: 'Активности', icon: '🏛️' },
                    { id: 'service', name: 'Сервис', icon: '🔧' }
                ];

                // Category usage statistics (локальная - для сортировки, сбрасывается)
                this.categoryStats = {
                    usage: {}, // { categoryId: count }
                    lastReorganization: null, // timestamp
                    reorganizationInterval: 3 * 24 * 60 * 60 * 1000 // 3 days in ms
                };

                // Template usage statistics (локальная - для сортировки услуг внутри категорий)
                this.templateStats = {
                    usage: {}, // { templateId: count }
                    lastReorganization: null, // timestamp
                    reorganizationInterval: 3 * 24 * 60 * 60 * 1000 // 3 days in ms
                };

                // Global statistics (НИКОГДА не сбрасывается - для аналитики)
                this.globalStats = {
                    services: {}, // { templateId: { count, name, category, lastUsed: timestamp } }
                    categories: {}, // { categoryId: { count, name, lastUsed: timestamp } }
                    totalServicesAdded: 0, // Общее количество добавленных услуг
                    firstUsage: null, // timestamp первого использования
                    lastUsage: null // timestamp последнего использования
                };

                this.templates = [];
                this.editingTemplateId = null;

                // Регионы для управления каталогами
                this.regions = [];
                this.currentRegion = 'Default';

                // Enhanced debounced functions with cleanup
                this.debouncedUpdate = this.debounce(this.updateCalculations.bind(this), this.config.debounceDelays.calculations, 'update');
                this.debouncedSearch = this.debounce(this.filterTemplates.bind(this), this.config.debounceDelays.search, 'search');
                this.debouncedRenderDetail = this.debounce(this.renderServicesDetailList.bind(this), this.config.debounceDelays.detailRender, 'detailRender');
                this.debouncedBulkUpdate = this.debounce(this.updateBulkControls.bind(this), this.config.debounceDelays.bulkControls, 'bulkUpdate');
                this.debouncedAutosave = this.debounce(this.autoSaveQuote.bind(this), this.config.debounceDelays.autosave, 'autosave');

                this.updatePending = false;

                this.init();
            }

            // Enhanced debounce with timeout tracking

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: UTILITIES
            //   Generic helpers: debounce, scheduleUpdate, showNotification, validation,
            //   formatting, ID generation, file naming. Mostly pure or with isolated DOM
            //   interaction.
            // ════════════════════════════════════════════════════════════════════

            debounce(func, wait, timeoutKey) {
                return (...args) => {
                    if (this.timeoutIds[timeoutKey]) {
                        clearTimeout(this.timeoutIds[timeoutKey]);
                    }
                    
                    this.timeoutIds[timeoutKey] = setTimeout(() => {
                        func.apply(this, args);
                        this.timeoutIds[timeoutKey] = null;
                    }, wait);
                };
            }

            scheduleUpdate() {
                if (this.updatePending) return;
                this.updatePending = true;
                requestAnimationFrame(() => {
                    this.updateCalculations();
                    this.updatePending = false;
                });
            }

            // Загрузить глобальные настройки при инициализации
            async loadGlobalSettings() {
                try {
                    const settings = await window.apiClient.loadSettings();
                    console.log('[Settings] Загружены глобальные настройки:', settings);

                    // Если есть сохраненные условия бронирования, применяем их только если текущие пусты
                    if (settings.bookingTerms && !this.state.bookingTerms) {
                        this.state.bookingTerms = settings.bookingTerms;

                        // Обновляем редактор если он уже инициализирован
                        if (this.quillEditors && this.quillEditors.bookingTerms) {
                            this.quillEditors.bookingTerms.root.innerHTML = settings.bookingTerms;
                        }
                    }
                } catch (err) {
                    console.warn('[Settings] Не удалось загрузить настройки:', err.message);
                    // Не показываем ошибку пользователю - это не критично
                }
            }

            // Отложенное сохранение глобальных настроек (debounce 3 секунды)
            scheduleGlobalSettingsSave() {
                if (this.globalSettingsSaveTimeout) {
                    clearTimeout(this.globalSettingsSaveTimeout);
                }

                this.globalSettingsSaveTimeout = setTimeout(async () => {
                    try {
                        const settings = {
                            bookingTerms: this.state.bookingTerms || '',
                            version: '1.0.0',
                            lastUpdated: new Date().toISOString()
                        };

                        await window.apiClient.saveSettings(settings);
                        console.log('[Settings] Глобальные настройки сохранены');
                    } catch (err) {
                        console.error('[Settings] Ошибка сохранения настроек:', err.message);
                        // Не показываем ошибку пользователю - автосохранение не критично
                    }
                }, 3000); // 3 секунды
            }

            showNotification(message, isError = false) {
                const notification = document.getElementById('notification');
                notification.textContent = message;
                notification.className = `notification ${isError ? 'error' : ''}`;
                
                // Show notification
                setTimeout(() => notification.classList.add('show'), 10);
                
                // Hide after configured timeout
                setTimeout(() => {
                    notification.classList.remove('show');
                }, this.config.ui.notificationTimeout);
            }

            validateService(service) {
                const errors = [];
                if (!service.name || !service.name.trim()) {
                    errors.push(_t('app.validation.service_name_required', 'Service name is required'));
                }
                if (service.price < 0) {
                    errors.push(_t('app.validation.price_negative', 'Price cannot be negative'));
                }
                if (service.quantity < 1) {
                    errors.push(_t('app.validation.quantity_min', 'Quantity must be greater than 0'));
                }
                if (service.day < 1) {
                    errors.push(_t('app.validation.day_min', 'Tour day must be greater than 0'));
                }
                return errors;
            }

            validateField(fieldId, value) {
                const element = document.getElementById(fieldId);
                if (!element) return;

                element.classList.remove('error');

                switch (fieldId) {
                    case 'pax-count':
                        if (!value || value < 1) {
                            element.classList.add('error');
                            this.showNotification(_t('app.validation.pax_min', 'Number of people must be greater than 0'), true);
                        }
                        break;
                    case 'client-email':
                        // Проверяем email только если поле не пустое
                        if (value && !this.isValidEmail(value)) {
                            element.classList.add('error');
                            element.title = _t('app.validation.email_invalid', 'Invalid email address');
                            // Не показываем notification при вводе, только визуальная индикация
                        } else if (value && this.isValidEmail(value)) {
                            element.classList.remove('error');
                            element.title = '';
                        }
                        break;
                    case 'tour-start':
                    case 'tour-end':
                        // Очищаем ошибки для обеих дат
                        const startElement = document.getElementById('tour-start');
                        const endElement = document.getElementById('tour-end');
                        if (startElement) startElement.classList.remove('error');
                        if (endElement) endElement.classList.remove('error');

                        // Проверяем диапазон дат
                        this.validateDateRange();
                        break;
                }
            }

            isValidEmail(email) {
                // Phase 2: делегирование к window.StringUtils
                return (window.StringUtils && window.StringUtils.isValidEmail)
                    ? window.StringUtils.isValidEmail(email)
                    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            }

            validateDateRange() {
                const startDate = this.state.tourStart;
                const endDate = this.state.tourEnd;

                if (startDate && endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);

                    if (end < start) {
                        const endElement = document.getElementById('tour-end');
                        if (endElement) endElement.classList.add('error');
                        this.showNotification(_t('app.validation.end_before_start', 'End date cannot be before start date'), true);
                        return false;
                    }
                }
                return true;
            }

            // Phase 2: делегирование к StringUtils (legacy-вызовы внутри класса работают как раньше)
            parseCSVLine(line) {
                return window.StringUtils.parseCSVLine(line);
            }

            escapeCSVField(field) {
                return window.StringUtils.escapeCSVField(field);
            }

            formatDate(dateString) {
                if (!dateString) return '';

                // Создаем дату без проблем с часовыми поясами
                const [year, month, day] = dateString.split('-');
                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

                return date.toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }

            // Рассчитать дату активности по дню тура
            calculateActivityDate(tourDay) {
                if (!this.state.tourStart || tourDay < 1) return '';

                const [year, month, day] = this.state.tourStart.split('-');
                const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

                // Добавляем (день - 1) дней к начальной дате
                const activityDate = new Date(startDate);
                activityDate.setDate(activityDate.getDate() + (tourDay - 1));

                return activityDate.toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }

            // Рендеринг иконки: emoji → Lucide
            renderIcon(emoji, size = '20px') {
                const lucideIcon = this.iconMap[emoji];
                if (lucideIcon) {
                    return `<i data-lucide="${lucideIcon}" style="width: ${size}; height: ${size}; display: inline-block;"></i>`;
                }
                return emoji; // Если нет маппинга, возвращаем сам emoji
            }

            // ===== UUID & FILENAME GENERATION - Генерация ID и имён файлов =====

            /**
             * Генерирует уникальный ID для сметы (UUID v4, 12 символов)
             * @returns {string} Уникальный ID
             */
            // ✅ FIX: Генератор настоящего UUID v4 (128 бит энтропии вместо 48)
            generateQuoteId() {
                // Phase 2: делегирование к StringUtils (использует crypto.randomUUID если доступно)
                return window.StringUtils.generateQuoteId();
            }

            /**
             * Генерирует имя файла из данных сметы с ID
             * @param {Object} quoteData - Данные сметы
             * @returns {string} Имя файла
             */
            generateFilenameWithId(quoteData) {
                const id = quoteData.id || this.state.currentQuoteId;
                const pax = quoteData.paxCount || this.state.paxCount || 0;
                const date = quoteData.tourStart || this.state.tourStart || new Date().toISOString().split('T')[0];

                // Транслитерация имени клиента
                let clientName = quoteData.clientName || this.state.clientName || '';
                clientName = clientName.trim();

                if (clientName === '') {
                    // Без ФИО - untitled
                    return `untitled_${date}_${pax}pax_${id}.json`;
                } else {
                    // С ФИО - транслитерация
                    const name = this.transliterate(clientName.toLowerCase()).replace(/\s+/g, '_');
                    return `${name}_${date}_${pax}pax_${id}.json`;
                }
            }

            /**
             * Транслитерация кириллицы в латиницу
             * @param {string} text - Текст для транслитерации
             * @returns {string} Транслитерированный текст
             */
            transliterate(text) {
                // Phase 2: делегирование к StringUtils
                return window.StringUtils.transliterate(text);
            }

            /**
             * Проверяет нужно ли переименовать файл при изменении данных
             * @returns {Promise<boolean>} true если файл был переименован
             */
            async checkAndRenameFile() {
                if (!this.state.currentQuoteId || !this.state.currentQuoteFile) {
                    return false; // Смета ещё не сохранена
                }

                const currentData = {
                    id: this.state.currentQuoteId,
                    clientName: this.state.clientName,
                    tourStart: this.state.tourStart,
                    paxCount: this.state.paxCount
                };

                const newFilename = this.generateFilenameWithId(currentData);

                if (this.state.currentQuoteFile !== newFilename) {
                    try {
                        // Переименовываем файл на сервере
                        await apiClient.renameEstimate(this.state.currentQuoteFile, newFilename);
                        this.state.currentQuoteFile = newFilename;
                        this.updateQuoteStatusBar();
                        return true;
                    } catch (err) {
                        console.error('Error renaming file:', err);
                        return false;
                    }
                }

                return false;
            }

            /**
             * Создаёт новую смету с уникальным ID
             */
            async createNewQuote() {
                // Генерируем ID
                this.state.currentQuoteId = this.generateQuoteId();

                // Сбрасываем данные
                this.state.services = [];
                this.state.hotels = [];
                this.state.flights = [];
                this.state.otherServices = [];
                this.state.optionalServices = []; // 🔧 FIX: добавлен сброс — раньше «прилипал» к новой смете
                this.state.clientName = '';
                this.state.clientPhone = '';
                this.state.clientEmail = '';
                this.state.paxCount = 0;
                this.state.tourStart = '';
                this.state.tourEnd = '';
                this.state.programDescription = '';
                this.state.quoteComments = '';
                this.state.bookingTerms = '';
                this.state.accommodations = {};
                // Сброс наценок и налогов — иначе переносятся между сметами
                this.state.hiddenMarkup = 0;
                this.state.partnerCommission = 0;
                this.state.taxRate = 0;
                this.state.selectedServices = new Set();

                // Генерируем начальное имя файла
                const filename = this.generateFilenameWithId({
                    id: this.state.currentQuoteId,
                    clientName: '',
                    tourStart: new Date().toISOString().split('T')[0],
                    paxCount: 0
                });

                this.state.currentQuoteFile = filename;
                this.state.isQuoteSaved = false;

                // Обновляем UI
                this.loadStateToUI();
                this.renderServices();
                this.renderHotels();
                this.renderFlights();
                this.renderOtherServices();
                this.updateCalculations();
                this.updateQuoteStatusBar();

                // Hide lifecycle status bar for new unsaved estimates
                const lifecycleBar = document.getElementById('estimate-lifecycle-bar-container');
                if (lifecycleBar) {
                    lifecycleBar.innerHTML = '';
                    lifecycleBar.style.display = 'none';
                }

                // Создаём начальный backup файл
                const initialData = {
                    id: this.state.currentQuoteId,
                    version: this.QUOTE_VERSION,
                    timestamp: new Date().toISOString(),
                    clientName: '',
                    clientPhone: '',
                    clientEmail: '',
                    paxCount: 0,
                    tourStart: '',
                    tourEnd: '',
                    services: [],
                    hotels: [],
                    flights: [],
                    otherServices: [],
                    hiddenMarkup: 0,
                    taxRate: 0,
                    programDescription: '',
                    quoteComments: ''
                };

                // Sprint 18 fix: НЕ создаём empty estimate в БД при createNewQuote().
                // Раньше тут был preemptive saveEstimate() — приводил к сотням
                // "Без имени" смет в БД (каждый клик "+ Создать" / открытие
                // /#/calculator?new=1 → пустая запись). Теперь:
                //   - state сброшен в initial/empty (см. выше)
                //   - state.currentQuoteId сгенерирован
                //   - markQuoteAsUnsaved() помечает forthcoming сохранение
                //   - autosave (autosave.js:172-175 guard) сохранит ТОЛЬКО когда
                //     юзер реально что-то введёт (clientName или services >0)
                // Так legitimate "new" flow работает (юзер вводит → autosave),
                // но "ghost" estimates больше не создаются.
                this.markQuoteAsUnsaved?.();
                this.showNotification(_t('app.toast.new_quote_hint', 'New quote — enter client data or add a service'), false);
            }

            /**
             * Восстанавливает последнюю рабочую смету после перезагрузки страницы
             * Проверяет последний изменённый backup (не старше 5 минут)
             */
            async restoreLastWorkingEstimate() {
                try {
                    // 1. Попытка восстановить конкретную смету из localStorage
                    const lastId = localStorage.getItem('lastOpenedEstimateId');

                    if (lastId) {
                        try {
                            console.log('[Restore] Attempting to restore estimate:', lastId);

                            // Загрузить смету по ID
                            const estimateData = await apiClient.getEstimate(lastId);

                            // 🔧 backward-compat: старые autosave-записи могли быть в nested-формате
                            const meta = estimateData.metadata || {};

                            // Применяем данные к состоянию
                            this.state.currentQuoteId = estimateData.id;
                            this.state.clientName = estimateData.clientName ?? meta.clientName ?? '';
                            this.state.clientPhone = estimateData.clientPhone ?? meta.clientPhone ?? '';
                            this.state.clientEmail = estimateData.clientEmail ?? meta.clientEmail ?? '';
                            this.state.paxCount = estimateData.paxCount ?? meta.paxCount ?? 0;
                            this.state.tourStart = estimateData.tourStart ?? meta.tourStart ?? '';
                            this.state.tourEnd = estimateData.tourEnd ?? meta.tourEnd ?? '';
                            this.state.hiddenMarkup = estimateData.hiddenMarkup ?? meta.hiddenMarkup ?? 0;
                            this.state.partnerCommission = estimateData.partnerCommission ?? meta.partnerCommission ?? 0;
                            this.state.taxRate = estimateData.taxRate ?? meta.taxRate ?? 0;
                            // Sprint 19a: prepayment + approvals
                            this.state.prepaymentManual = estimateData.prepaymentManual ?? meta.prepaymentManual ?? 0;
                            this.state.prepaymentApprovedAt = estimateData.prepaymentApprovedAt ?? null;
                            this.state.prepaymentApprovedBy = estimateData.prepaymentApprovedBy ?? null;
                            this.state.finalPaymentApprovedAt = estimateData.finalPaymentApprovedAt ?? null;
                            this.state.finalPaymentApprovedBy = estimateData.finalPaymentApprovedBy ?? null;
                            this.state.programDescription = estimateData.programDescription ?? meta.programDescription ?? '';
                            this.state.quoteComments = estimateData.quoteComments ?? meta.quoteComments ?? '';
                            this.state.bookingTerms = estimateData.bookingTerms ?? meta.bookingTerms ?? '';
                            this.state.accommodations = estimateData.accommodations ?? meta.accommodations ?? {};
                            this.state.services = estimateData.services || [];
                            this.state.hotels = estimateData.hotels || [];
                            this.state.flights = estimateData.flights || [];
                            this.state.otherServices = estimateData.otherServices || [];
                            this.state.optionalServices = estimateData.optionalServices || [];

                            // Генерируем имя файла
                            const filename = this.generateFilenameWithId({
                                id: estimateData.id,
                                clientName: estimateData.clientName,
                                tourStart: estimateData.tourStart,
                                paxCount: estimateData.paxCount
                            });

                            this.state.currentQuoteFile = filename;
                            this.state.isQuoteSaved = true;

                            // Обновляем UI
                            this.loadStateToUI();
                            this.renderServices();
                            this.renderHotels();
                            this.renderFlights();
                            this.renderOtherServices();
                            this.updateCalculations();
                            this.updateQuoteStatusBar();

                            // Estimate Lifecycle Status Bar (Sprint 19)
                            const statusBarContainer = document.getElementById('estimate-lifecycle-bar-container');
                            if (statusBarContainer && window.EstimateStatusBar && window.apiClient) {
                                const estimateId = estimateData.id || this.state.currentQuoteId;
                                const renderLifecycleBar = (statusData) => {
                                    statusBarContainer.innerHTML = '';
                                    statusBarContainer.style.display = '';
                                    const bar = window.EstimateStatusBar.render(statusData);
                                    statusBarContainer.appendChild(bar);
                                    window.EstimateStatusBar.bind(estimateId, window.apiClient, (result) => {
                                        renderLifecycleBar({
                                            status: result.to_status,
                                            allowedTransitions: result.allowedTransitions || [],
                                            nextStatus: result.nextStatus || null,
                                            prepaymentApprovedAt: this.state.prepaymentApprovedAt
                                        });
                                    });
                                };
                                renderLifecycleBar({
                                    status: estimateData.status || 'draft',
                                    allowedTransitions: estimateData.allowedTransitions || [],
                                    nextStatus: estimateData.nextStatus || null,
                                    prepaymentApprovedAt: estimateData.prepaymentApprovedAt,
                                    bookingsCompleteAt: estimateData.bookingsCompleteAt,
                                    tourStartedAt: estimateData.tourStartedAt,
                                    tourEndedAt: estimateData.tourEndedAt,
                                    suppliersPaidAt: estimateData.suppliersPaidAt,
                                    reconciliationDoneAt: estimateData.reconciliationDoneAt
                                });
                            }

                            // Показываем уведомление
                            const clientName = estimateData.clientName || _t('app.client.unnamed', 'Unnamed');
                            this.showNotification(`${_t('app.toast.quote_restored', 'Quote restored')}: ${clientName}`, false);

                            console.log('[Restore] Successfully restored estimate:', lastId);
                            return true;

                        } catch (err) {
                            console.warn('[Restore] Last estimate not found, clearing localStorage:', err);
                            localStorage.removeItem('lastOpenedEstimateId');
                            // Продолжаем к fallback
                        }
                    }

                    // 2. Fallback: пустое состояние (новая смета)
                    console.log('[Restore] No last estimate found, starting with empty state');
                    return false;

                } catch (err) {
                    console.error('[Restore] Error restoring estimate:', err);
                    return false;
                }
            }


            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: LIFECYCLE & MODE
            //   Initialization, mode switching (quote/checklist), keyboard shortcuts, modal
            //   closing, top-level UI orchestration.
            // ════════════════════════════════════════════════════════════════════

            init() {
                // ✅ FIX: Установить ссылку на window.apiClient для catalog operations
                this.apiClient = window.apiClient;

                this.initRegions(); // Инициализация регионов (должна быть первой!)
                // loadCategories() не нужен - loadCatalogForRegion() уже загружает категории
                // renderCategories() и renderCategoryOptions() вызываются в loadCatalogForRegion() после загрузки данных
                this.loadExternalCatalog();
                this.bindEvents();
                // Sprint 18 lazy-load: Quill редакторы инициализируются по требованию
                // через shell.js при входе на route 'calculator' (см. setActiveRoute).
                // Это экономит 48KB gzip + ~1.3s cold-load на routes без редактора
                // (estimates list, dashboard, tracker, admin).
                this.loadGlobalSettings(); // Загрузка глобальных настроек (условия бронирования)
                this.bindKeyboardShortcuts();
                this.updateCalculations();
                // loadFromLocalStorage теперь async (загружает автосохранение с сервера)
                this.loadFromLocalStorage().catch(err => {
                    console.error('Error loading from localStorage:', err);
                });
                this.migrateServicesRegions(); // Миграция данных: добавляем region к существующим услугам
                this.initVersionDisplay();
                this.restoreProgramDescriptionState();
                this.restoreQuoteCommentsState();
                this.restoreBookingTermsState();
                this.loadSavedCatalogFolder();

                // Рендерим новые секции
                this.renderHotels();
                this.renderFlights();
                this.renderOtherServices();
                this.initChecklistEvents(); // Инициализация событий чеклиста
                this.initNewSectionsEvents(); // Инициализация событий новых секций

                // 🆕 Phase 9: прогрев кеша поставщиков для рендеров
                if (typeof this._loadSuppliersCache === 'function') {
                    this._loadSuppliersCache().catch(err => {
                        console.warn('Suppliers cache load failed:', err);
                    });
                }

                // Восстанавливаем последнюю рабочую смету после перезагрузки
                this.restoreLastWorkingEstimate().catch(err => {
                    console.error('Error restoring last working estimate:', err);
                });

                // Восстанавливаем последний активный режим (смета/чеклист)
                // Если был сохранен режим чеклиста, переключаемся на него
                if (this.state.currentMode === 'checklist') {
                    this.switchMode('checklist');
                } else {
                    // По умолчанию режим сметы, просто обновляем UI
                    this.switchMode('quote');
                }
            }

            // ===== MODE SWITCHING - Переключение режимов (Смета/Чеклист) =====
            switchMode(mode) {
                this.state.currentMode = mode;

                // Обновляем стили кнопок переключателя
                const quoteBtn = document.getElementById('mode-quote-btn');
                const checklistBtn = document.getElementById('mode-checklist-btn');

                if (mode === 'quote') {
                    // Активируем режим сметы
                    quoteBtn.style.background = 'white';
                    quoteBtn.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    quoteBtn.style.color = 'inherit';
                    checklistBtn.style.background = 'transparent';
                    checklistBtn.style.boxShadow = 'none';
                    checklistBtn.style.color = 'var(--gray-600)';

                    // Показываем панели сметы, скрываем чеклист
                    const panels = document.querySelectorAll('.app > .panel');
                    panels.forEach((panel, index) => {
                        if (index < 3) { // Первые 3 панели - это смета
                            panel.style.display = '';
                        }
                    });
                    document.getElementById('checklist-container').style.display = 'none';
                } else if (mode === 'checklist') {
                    // Активируем режим чеклиста
                    checklistBtn.style.background = 'white';
                    checklistBtn.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    checklistBtn.style.color = 'inherit';
                    quoteBtn.style.background = 'transparent';
                    quoteBtn.style.boxShadow = 'none';
                    quoteBtn.style.color = 'var(--gray-600)';

                    // Скрываем панели сметы, показываем чеклист
                    const panels = document.querySelectorAll('.app > .panel');
                    panels.forEach((panel, index) => {
                        if (index < 3) { // Первые 3 панели - это смета
                            panel.style.display = 'none';
                        }
                    });
                    const checklistContainer = document.getElementById('checklist-container');
                    if (checklistContainer) {
                        checklistContainer.style.display = 'flex';
                    }

                    // Рендерим чеклист
                    this.renderChecklist();
                }

                // Сохраняем режим в localStorage
                localStorage.setItem('quoteCalc_currentMode', mode);
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: CHECKLIST (extracted Phase 5)
            //   📦 groupByDay, groupBySupplier, findServiceInState,
            //      passesChecklistFilter, updateChecklistCounters,
            //      renderChecklist, renderChecklistGroup, renderChecklistServiceCard
            //   → public/js/app/checklist.js
            // ════════════════════════════════════════════════════════════════════

            async loadSavedCatalogFolder() {
                try {
                    // Загружаем handle из IndexedDB
                    const handle = await this.loadFolderHandleFromIndexedDB();

                    if (handle) {
                        // Проверяем, есть ли ещё доступ к папке
                        const permission = await handle.queryPermission({ mode: 'readwrite' });

                        if (permission === 'granted') {
                            // Доступ есть, используем handle
                            this.catalogDirectoryHandle = handle;
                            this.state.saveFolder = handle.name;
                            console.log('Папка каталога восстановлена:', handle.name);
                        } else {
                            // Доступ потерян - сохраняем только имя для отображения предупреждения
                            console.log('Доступ к папке потерян, требуется повторный выбор');
                            this.state.saveFolder = handle.name;
                            this.catalogDirectoryHandle = null;
                        }
                    } else {
                        // Загружаем только имя из localStorage для отображения
                        const savedFolder = localStorage.getItem('quoteCalc_catalogFolder');
                        if (savedFolder) {
                            this.state.saveFolder = savedFolder;
                            console.log('Загружено имя папки (без доступа):', savedFolder);
                        }
                    }
                } catch (error) {
                    console.log('Не удалось восстановить папку:', error.message);
                    // Пробуем хотя бы загрузить имя
                    const savedFolder = localStorage.getItem('quoteCalc_catalogFolder');
                    if (savedFolder) {
                        this.state.saveFolder = savedFolder;
                    }
                }

                // Обновляем UI после загрузки
                this.updateCatalogFolderDisplay();
            }

            initVersionDisplay() {
                const versionElement = document.getElementById('app-version');
                if (versionElement) {
                    versionElement.textContent = `v${this.APP_VERSION}`;
                }
            }

            initChecklistEvents() {
                // Filter buttons
                const filterBtns = document.querySelectorAll('.checklist-filter-btn');
                filterBtns.forEach(btn => {
                    btn.addEventListener('click', () => {
                        // Remove active from all
                        filterBtns.forEach(b => {
                            b.style.background = 'transparent';
                            b.style.color = 'var(--gray-600)';
                            b.classList.remove('active');
                        });

                        // Set active
                        btn.style.background = 'white';
                        btn.style.color = 'inherit';
                        btn.classList.add('active');

                        // Update state and re-render
                        this.state.checklistFilter = btn.dataset.filter;
                        this.renderChecklist();
                    });
                });

                // View mode buttons
                const viewBtns = document.querySelectorAll('.checklist-view-btn');
                viewBtns.forEach(btn => {
                    btn.addEventListener('click', () => {
                        // Remove active from all
                        viewBtns.forEach(b => {
                            b.style.background = 'transparent';
                            b.style.color = 'var(--gray-600)';
                            b.classList.remove('active');
                        });

                        // Set active
                        btn.style.background = 'white';
                        btn.style.color = 'inherit';
                        btn.classList.add('active');

                        // Update state and re-render
                        this.state.checklistViewMode = btn.dataset.view;
                        this.renderChecklist();
                    });
                });
            }

            initNewSectionsEvents() {
                console.log('[EVENTS] initNewSectionsEvents() called');
                // Универсальная функция для обработки событий секций
                const initSectionEvents = (container, arrayName, renderFunc, type) => {
                    console.log(`[EVENTS] initSectionEvents for type: ${type}, arrayName: ${arrayName}, renderFunc: ${renderFunc.name}`);
                    if (!container) return;

                    // Click events
                    container.addEventListener('click', (e) => {
                        const serviceCard = e.target.closest('.service-card');
                        if (!serviceCard) return;
                        const serviceId = serviceCard.dataset.serviceId;

                        // Remove button
                        if (e.target.closest(`.remove-${type}-btn`)) {
                            this.state[arrayName] = this.state[arrayName].filter(s => s.id !== serviceId);
                            this.state.selectedServices.delete(serviceId);
                            renderFunc.call(this);
                            this.scheduleUpdate();
                            this.markQuoteAsUnsaved();
                            return;
                        }

                        // Duplicate button
                        if (e.target.closest(`.duplicate-${type}-btn`)) {
                            const service = this.state[arrayName].find(s => s.id === serviceId);
                            if (service) {
                                const newService = {
                                    ...service,
                                    id: Date.now().toString(),
                                    name: service.name + ' ' + _t('app.copy_suffix', '(copy)')
                                };
                                this.state[arrayName].unshift(newService);
                                renderFunc.call(this);
                                this.scheduleUpdate();
                                this.markQuoteAsUnsaved();
                                this.showNotification(_t('app.toast.service_duplicated', 'Service duplicated'));
                            }
                            return;
                        }

                        // Booking status button
                        if (e.target.closest(`.${type}-booking-status-btn`)) {
                            const service = this.state[arrayName].find(s => s.id === serviceId);
                            if (service) {
                                service.done = !service.done;
                                renderFunc.call(this);
                                this.scheduleUpdate();
                                this.markQuoteAsUnsaved();
                            }
                            return;
                        }

                        // Payment status button
                        if (e.target.closest(`.${type}-payment-status-btn`)) {
                            const service = this.state[arrayName].find(s => s.id === serviceId);
                            if (service) {
                                service.paid = !service.paid;
                                renderFunc.call(this);
                                this.scheduleUpdate();
                                this.markQuoteAsUnsaved();
                            }
                            return;
                        }

                        // Timing toggle button
                        if (e.target.closest(`.${type}-timing-toggle-btn`)) {
                            console.log(`[CONSTRUCTOR] Timing toggle clicked for ${type}-${serviceId}`);
                            const timingContent = document.getElementById(`${type}-timing-${serviceId}`);
                            const icon = e.target.closest(`.${type}-timing-toggle-btn`).querySelector('.timing-toggle-icon');
                            if (timingContent) {
                                const isHidden = timingContent.style.maxHeight === '0px' || !timingContent.style.maxHeight;
                                console.log(`[CONSTRUCTOR] Toggle: ${isHidden ? 'expanding' : 'collapsing'}`);
                                timingContent.style.maxHeight = isHidden ? '200px' : '0';
                                if (icon) icon.textContent = isHidden ? '▼' : '▶';
                            }
                            return;
                        }

                        // Quantity buttons
                        if (e.target.classList.contains('qty-decrease') || e.target.classList.contains('qty-increase')) {
                            const service = this.state[arrayName].find(s => s.id === serviceId);
                            if (service) {
                                if (e.target.classList.contains('qty-decrease')) {
                                    service.quantity = Math.max(1, service.quantity - 1);
                                } else {
                                    service.quantity = service.quantity + 1;
                                }
                                renderFunc.call(this);
                                this.scheduleUpdate();
                                this.markQuoteAsUnsaved();
                            }
                            return;
                        }
                    });

                    // Change events
                    container.addEventListener('change', (e) => {
                        const serviceId = e.target.dataset.serviceId;
                        const service = this.state[arrayName].find(s => s.id === serviceId);
                        if (!service) return;

                        // Input fields
                        if (e.target.classList.contains(`${type}-field`)) {
                            const field = e.target.dataset.field;
                            console.log(`[CONSTRUCTOR] Field changed: ${field}, value: ${e.target.value}, type: ${type}, serviceId: ${serviceId}`);

                            if (['price', 'quantity', 'markup', 'prepayment', 'day', 'roomsCount'].includes(field)) {
                                service[field] = parseFloat(e.target.value) || 0;
                            } else {
                                service[field] = e.target.value;
                            }

                            // Update duration if startTime or endTime changed
                            // НЕ перерисовываем карточку для полей времени - только обновляем duration
                            if (field === 'startTime' || field === 'endTime') {
                                console.log(`[CONSTRUCTOR] Time field detected - updating duration ONLY, NO RERENDER`);
                                const durationEl = document.getElementById(`${type}-duration-${serviceId}`);
                                if (durationEl) {
                                    durationEl.textContent = this.calculateDuration(service.startTime, service.endTime);
                                    console.log(`[CONSTRUCTOR] Duration updated to: ${durationEl.textContent}`);
                                } else {
                                    console.warn(`[CONSTRUCTOR] Duration element not found: ${type}-duration-${serviceId}`);
                                }
                                // Только обновляем расчёты, НЕ перерисовываем
                                this.scheduleUpdate();
                                this.markQuoteAsUnsaved();
                                console.log(`[CONSTRUCTOR] Time field change complete - NO RERENDER called`);
                                return;
                            }

                            console.log(`[CONSTRUCTOR] Non-time field - calling RERENDER`);
                            console.trace('[CONSTRUCTOR] Stack trace for renderFunc call:');
                            renderFunc.call(this);
                            this.scheduleUpdate();
                            this.markQuoteAsUnsaved();
                            return;
                        }

                        // Region select
                        if (e.target.classList.contains(`${type}-region-select`)) {
                            service.region = e.target.value;
                            renderFunc.call(this);
                            this.scheduleUpdate();
                            this.markQuoteAsUnsaved();
                            return;
                        }

                        // Checkboxes
                        if (e.target.classList.contains(`${type}-checkbox`)) {
                            if (e.target.checked) {
                                this.state.selectedServices.add(serviceId);
                            } else {
                                this.state.selectedServices.delete(serviceId);
                            }
                            renderFunc.call(this);
                            return;
                        }

                        if (e.target.classList.contains(`${type}-exclude-checkbox`)) {
                            service.excludeFromMarkup = e.target.checked;
                            renderFunc.call(this);
                            this.scheduleUpdate();
                            this.markQuoteAsUnsaved();
                            return;
                        }

                        if (e.target.classList.contains(`${type}-full-profit-checkbox`)) {
                            service.fullProfit = e.target.checked;
                            renderFunc.call(this);
                            this.scheduleUpdate();
                            this.markQuoteAsUnsaved();
                            return;
                        }
                    });

                    // Blur events for contenteditable
                    container.addEventListener('blur', (e) => {
                        if (e.target.classList.contains('editable')) {
                            const serviceId = e.target.dataset.id;
                            const field = e.target.dataset.field;
                            const service = this.state[arrayName].find(s => s.id === serviceId);
                            if (service) {
                                service[field] = e.target.textContent.trim();
                                this.markQuoteAsUnsaved();
                            }
                        }
                    }, true);
                };

                // Инициализация для каждой секции
                initSectionEvents(
                    document.getElementById('hotels-list'),
                    'hotels',
                    this.renderHotels,
                    'hotel'
                );

                initSectionEvents(
                    document.getElementById('flights-list'),
                    'flights',
                    this.renderFlights,
                    'flight'
                );

                initSectionEvents(
                    document.getElementById('other-services-list'),
                    'otherServices',
                    this.renderOtherServices,
                    'other'
                );
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: KEYBOARD SHORTCUTS
            //   📦 Извлечено в public/js/app/keyboard.js (Sprint 15 C.3):
            //     - bindKeyboardShortcuts, closeAllModals
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: COLLAPSIBLES (program description / quote comments / booking terms)
            //   📦 Извлечено в public/js/app/collapsibles.js (Sprint 15 C.3):
            //     - toggleProgramDescription / toggleQuoteComments / toggleBookingTerms
            //     - restoreProgramDescriptionState / restoreQuoteCommentsState
            //     - restoreBookingTermsState / updateCollapsibleHeight
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: QUILL EDITORS
            //   📦 Извлечено в public/js/app/quill-editors.js (Phase 5):
            //     - initQuillEditors / addQuillTooltips / applyTooltipsToToolbar
            //   Quill подключается как CDN classic-script (window.Quill).
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: EVENT BINDING (extracted Phase 5)
            //   📦 bindEvents() — single point of DOM event-handlers init
            //   → public/js/app/event-binding.js
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: CATALOG TEMPLATES (часть 1)
            //   📦 Извлечено в public/js/app/catalog-templates.js (Phase 5):
            //     - renderTemplates / addServiceFromTemplate (+ Optional / Other)
            //   Остальные catalog-методы (showCatalogModal, editTemplate, addToCatalog,
            //   filterTemplates, renderCategories) — пока в app.js.
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: SERVICE MODAL + OPTIONAL SERVICES
            //   📦 Извлечено в public/js/app/modals.js (Sprint 15 C.3):
            //     - showServiceModal / hideServiceModal / addCustomService
            //     - showOptionalServiceModal / addOptionalService / renderOptionalServices
            //     - moveOptionalToMain / updateOptionalServiceField / removeOptionalService
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: BULK OPERATIONS
            //   📦 Извлечено в public/js/app/bulk-operations.js (Phase 5):
            //     - toggleServiceSelection / toggleSelectAll
            //     - updateBulkControls / deleteSelected
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: RENDER — SERVICES LIST (extracted Phase 5)
            //   📦 renderServices, renderServicesFlat, renderServicesTable,
            //      bindServiceEvents, renderServicesDetailList
            //   → public/js/app/render-services-list.js
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: CALCULATIONS — PER-SERVICE
            //   📦 Извлечено в public/js/app/calculations.js (Phase 5).
            //   Методы добавляются на prototype через attachCalculations()
            //   из entry.js. Pure-versions: public/js/utils/calc-utils.js.
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: SERVICE FIELDS (per-service editing in main services list)
            //   📦 Извлечено в public/js/app/service-fields.js (Sprint 15 C.3):
            //     - handleEdit / updateServiceRegion / updateServiceField / changeQuantity
            //     - toggleServiceNoDayAssignment / toggleServiceExclusion / toggleServiceFullProfit
            //     - moveServiceToOther / toggleBookingStatus / togglePaymentStatus
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ PHASE 8a — Чеклист-интеграция в режим Сметы (cards mode)
            // ════════════════════════════════════════════════════════════════════

            /**
             * Возвращает все услуги объединённо: services + hotels + flights + otherServices.
             * Используется в updateChecklistStats() и для подсчёта счётчиков в quick-filter buttons.
             */
            getAllServicesFlat() {
                return [
                    ...(this.state.services || []),
                    ...(this.state.hotels || []),
                    ...(this.state.flights || []),
                    ...(this.state.otherServices || [])
                ];
            }

            /**
             * Проверяет, проходит ли услуга текущий quick-filter (cards mode).
             * 'all' — все, 'not_booked' — !done, 'not_paid' — !paid
             */
            passesQuickFilter(service) {
                const f = this.state.checklistQuickFilter;
                if (f === 'not_booked') return !service.done;
                if (f === 'not_paid') return !service.paid;
                return true;
            }

            /**
             * Установить quick-filter и перерисовать все секции с услугами.
             */
            setChecklistQuickFilter(value) {
                if (!['all', 'not_booked', 'not_paid'].includes(value)) return;
                this.state.checklistQuickFilter = value;
                this.renderServices();
                this.renderHotels();
                this.renderFlights();
                this.renderOtherServices();
                this.updateQuickFilterButtons();
            }

            /**
             * Обновить визуальное состояние quick-filter buttons (active / counts).
             */
            updateQuickFilterButtons() {
                const all = this.getAllServicesFlat();
                const notBooked = all.filter(s => !s.done).length;
                const notPaid = all.filter(s => !s.paid).length;

                const cntNotBookedEl = document.getElementById('qf-count-not-booked');
                const cntNotPaidEl = document.getElementById('qf-count-not-paid');
                if (cntNotBookedEl) cntNotBookedEl.textContent = notBooked;
                if (cntNotPaidEl) cntNotPaidEl.textContent = notPaid;

                // Подсветка активной кнопки
                document.querySelectorAll('.quick-filter-btn').forEach(btn => {
                    const isActive = btn.dataset.qfilter === this.state.checklistQuickFilter;
                    btn.classList.toggle('active', isActive);
                    btn.style.background = isActive ? 'white' : 'transparent';
                    btn.style.color = isActive ? 'inherit' : 'var(--gray-600)';
                });
            }

            /**
             * Обновить статистику «Статус заказов» в правой панели.
             * Вызывается из updateCalculations() после блока расчёта прибыли.
             */
            updateChecklistStats() {
                const all = this.getAllServicesFlat();
                const total = all.length;
                const booked = all.filter(s => s.done).length;
                const paid = all.filter(s => s.paid).length;

                // Сумма not-booked / not-paid (с учётом наценки на услугу).
                let notBookedAmount = 0;
                let notPaidAmount = 0;
                all.forEach(s => {
                    const totalForService = this.calculateServiceTotal(s);
                    if (!s.done) notBookedAmount += totalForService;
                    if (!s.paid) {
                        const prepaymentReceived = (s.prepayment || 0) * (s.prepaymentReceived ? 1 : 0);
                        notPaidAmount += Math.max(0, totalForService - prepaymentReceived);
                    }
                });

                const elBooked = document.getElementById('stats-booked');
                const elPaid = document.getElementById('stats-paid');
                const elTotal1 = document.getElementById('stats-total-1');
                const elTotal2 = document.getElementById('stats-total-2');
                const elNotBookedAmount = document.getElementById('stats-not-booked-amount');
                const elNotPaidAmount = document.getElementById('stats-not-paid-amount');

                if (elBooked) elBooked.textContent = booked;
                if (elPaid) elPaid.textContent = paid;
                if (elTotal1) elTotal1.textContent = total;
                if (elTotal2) elTotal2.textContent = total;
                if (elNotBookedAmount) elNotBookedAmount.textContent = this.formatCurrency(notBookedAmount);
                if (elNotPaidAmount) elNotPaidAmount.textContent = this.formatCurrency(notPaidAmount);

                // Также обновим счётчики в quick-filter buttons
                this.updateQuickFilterButtons();
            }

            toggleServiceTiming(serviceId) {
                const content = document.getElementById(`timing-${serviceId}`);
                const btn = document.querySelector(`.timing-toggle-btn[data-service-id="${serviceId}"]`);
                const icon = btn?.querySelector('.timing-toggle-icon');

                if (!content) return;

                const isExpanded = content.style.maxHeight && content.style.maxHeight !== '0px';

                if (isExpanded) {
                    content.style.maxHeight = '0';
                    if (icon) icon.textContent = '▶';
                } else {
                    content.style.maxHeight = content.scrollHeight + 'px';
                    if (icon) icon.textContent = '▼';
                }
            }

            removeService(serviceId) {
                // Fixed: Add cleanup for selectedServices
                this.state.services = this.state.services.filter(s => s.id !== serviceId);
                this.state.selectedServices.delete(serviceId); // Clean up selection
                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
                this.showNotification(_t('app.toast.service_deleted', 'Service deleted'));
            }

            duplicateService(serviceId) {
                const serviceIndex = this.state.services.findIndex(s => s.id === serviceId);

                if (serviceIndex === -1) {
                    this.showNotification(_t('app.toast.service_not_found', 'Service not found'), true);
                    return;
                }

                const originalService = this.state.services[serviceIndex];

                // Создать глубокую копию с новым ID
                const duplicatedService = {
                    ...originalService,
                    id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9),
                    // Копируем все поля включая новые
                    done: originalService.done || false,
                    paid: originalService.paid || false,
                    startTime: originalService.startTime || '',
                    endTime: originalService.endTime || '',
                    noDayAssignment: originalService.noDayAssignment || false
                };

                // Вставить дублированную услугу сразу после оригинала
                this.state.services.splice(serviceIndex + 1, 0, duplicatedService);

                this.renderServices();
                this.scheduleUpdate();
                this.markQuoteAsUnsaved();
                this.showNotification('📋 ' + _t('app.toast.service_duplicated', 'Service duplicated'));
            }


            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: UPDATE CALCULATIONS — ORCHESTRATION
            //   Top-level calc dispatcher; updates DOM with totals after any change.
            // ════════════════════════════════════════════════════════════════════

            updateCalculations() {
                // ════════════════════════════════════════════════════════════════
                // SPRINT 18 BUG FIX (2026-05-09):
                //   Раньше baseCost (отображается как "Себестоимость") включал
                //   fullProfit-услуги по их serviceWithMarkup. Это ломало математику
                //   дисплея: Себестоимость + Прибыль ≠ ИТОГО клиенту, потому что
                //   fullProfit услуги учитывались дважды (в Себестоимости И в Прибыли).
                //
                //   Пример: смета "staryh_viktorovy" — 13 обычных услуг (4389) +
                //   3 × "Работа гида" по 400 (fullProfit). До fix:
                //     Себестоимость: 5589  (включает 1200 гидов)
                //     Прибыль:        1200
                //     ИТОГО клиенту:  5589 → math broken (cost+profit > total на 1200)
                //   После fix:
                //     Себестоимость:  4389  (только реальные расходы)
                //     Прибыль:        1200
                //     ИТОГО клиенту:  5589 ✓ math работает
                //
                //   ВАЖНО: внутренние формулы totalWithIndividualMarkup / clientTotal /
                //   partnerCommissionAmount остаются нетронутыми — fix только в том,
                //   ЧТО мы показываем в "cost-price" (`displayedCost`, без fullProfit).
                //   `baseCost` сохранён для partner commission (backward compat).
                // ════════════════════════════════════════════════════════════════

                let baseCost = 0;                  // для partner commission (включает fullProfit×markup) — legacy
                let displayedCost = 0;             // ← НОВОЕ: для UI cost-price (исключает fullProfit)
                let baseCostForHiddenMarkup = 0;   // для скрытой маржи (исключает fullProfit и excludeFromMarkup)
                let totalWithIndividualMarkup = 0;
                let individualMarkupAmount = 0;
                let fullProfitAmount = 0;          // сумма от услуг "вся сумма в прибыль"

                // Объединяем все массивы услуг для расчёта
                const allServices = [
                    ...(this.state.services || []),
                    ...(this.state.hotels || []),
                    ...(this.state.flights || []),
                    ...(this.state.otherServices || [])
                ];

                allServices.forEach(service => {
                    const serviceCost = service.price * service.quantity;
                    const serviceWithMarkup = this.calculateServiceTotal(service);
                    const serviceMarkupAmount = serviceWithMarkup - serviceCost;

                    // Если "вся сумма в прибыль" - вся сумма идёт в прибыль, но участвует в расчёте комиссии
                    if (service.fullProfit) {
                        fullProfitAmount += serviceWithMarkup;
                        totalWithIndividualMarkup += serviceWithMarkup;
                        baseCost += serviceWithMarkup;  // legacy: для partner commission
                        // displayedCost НЕ инкрементим — fullProfit услуги это не себестоимость
                    } else {
                        baseCost += serviceCost;
                        displayedCost += serviceCost;   // обычные услуги учитываются в Себестоимости
                        totalWithIndividualMarkup += serviceWithMarkup;
                        individualMarkupAmount += serviceMarkupAmount; // Всегда добавляем индивидуальные наценки

                        // Добавляем в базу для общей наценки только если услуга НЕ исключена
                        if (!service.excludeFromMarkup) {
                            baseCostForHiddenMarkup += serviceCost;
                        }
                    }
                });

                // Скрытая маржа применяется только к неисключенным услугам
                const hiddenMarkupAmount = baseCostForHiddenMarkup * (this.state.hiddenMarkup / 100);

                // Комиссия партнера рассчитывается от СЕБЕСТОИМОСТИ
                const partnerCommissionAmount = baseCost * (this.state.partnerCommission / 100);

                // Сумма после добавления скрытой маржи и комиссии партнера
                const totalWithHiddenMarkup = totalWithIndividualMarkup + hiddenMarkupAmount + partnerCommissionAmount;

                // Итого для клиента (без НДС)
                const clientTotal = totalWithHiddenMarkup;

                // Общая прибыль = индивидуальные наценки + скрытая маржа + услуги "вся сумма в прибыль"
                const totalProfit = individualMarkupAmount + hiddenMarkupAmount + fullProfitAmount;


                // Visual feedback for updates
                const grandTotal = document.getElementById('grand-total');
                if (grandTotal) {
                    grandTotal.classList.add('updating');
                    setTimeout(() => grandTotal.classList.remove('updating'), 300);
                }

                // Update individual calculation blocks with null checks
                const costPrice = document.getElementById('cost-price');
                if (costPrice) {
                    // Sprint 18 fix: показываем РЕАЛЬНУЮ себестоимость (без fullProfit услуг).
                    // Иначе fullProfit-услуги показывались как cost И как profit одновременно.
                    costPrice.textContent = this.formatCurrency(displayedCost);
                }

                // Sprint 19a: auto-calc prepayment from services with requires_prepayment flag
                const prepaymentAuto = (this.state.services || []).reduce((sum, svc) => {
                    if (svc.requires_prepayment) {
                        const price = Number(svc.price) || 0;
                        const qty = Number(svc.quantity) || 1;
                        const markup = 1 + (Number(svc.markup) || 0) / 100;
                        return sum + price * qty * markup;
                    }
                    return sum;
                }, 0);
                const prepaymentManual = Number(this.state.prepaymentManual) || 0;
                const prepaymentTotal = prepaymentAuto + prepaymentManual;

                const prepaymentAutoEl = document.getElementById('prepayment-auto-amount');
                if (prepaymentAutoEl) prepaymentAutoEl.textContent = this.formatCurrency(prepaymentAuto);

                // Show/hide and populate the prepayment row in plate
                const prepaymentRow = document.getElementById('prepayment-total-row');
                const prepaymentTotalEl = document.getElementById('prepayment-total');
                const prepaymentAutoDisp = document.getElementById('prepayment-auto-display');
                const prepaymentManualDisp = document.getElementById('prepayment-manual-display');
                if (prepaymentRow) {
                    if (prepaymentTotal > 0) {
                        prepaymentRow.style.display = 'block';
                        if (prepaymentTotalEl) prepaymentTotalEl.textContent = this.formatCurrency(prepaymentTotal);
                        if (prepaymentAutoDisp) prepaymentAutoDisp.textContent = this.formatCurrency(prepaymentAuto);
                        if (prepaymentManualDisp) prepaymentManualDisp.textContent = this.formatCurrency(prepaymentManual);
                    } else {
                        prepaymentRow.style.display = 'none';
                    }
                }
                this._prepaymentAuto = prepaymentAuto;
                this._prepaymentTotal = prepaymentTotal;

                // Sprint 18: sanity-check математика — Cost + Profit должны равняться
                // ИТОГО клиенту (с погрешностью на rounding). Если нет — логируем warning.
                const totalProfitForCheck = individualMarkupAmount + hiddenMarkupAmount + fullProfitAmount;
                const expectedTotal = displayedCost + totalProfitForCheck;
                const actualTotal = totalWithIndividualMarkup + hiddenMarkupAmount;  // partner commission не показывается клиенту
                if (Math.abs(expectedTotal - actualTotal) > 1) {
                    console.warn('[Calc] Math mismatch:', {
                        displayedCost: Math.round(displayedCost),
                        profit: Math.round(totalProfitForCheck),
                        expectedTotal: Math.round(expectedTotal),
                        actualClientTotal: Math.round(actualTotal),
                        diff: Math.round(expectedTotal - actualTotal)
                    });
                }

                // Individual markup info
                const individualInfo = document.getElementById('individual-markup-info');
                const individualAmount = document.getElementById('individual-markup-amount');
                const totalWithMarkupElement = document.getElementById('total-with-markup');
                if (individualInfo && individualAmount && totalWithMarkupElement) {
                    if (individualMarkupAmount > 0) {
                        individualInfo.style.display = 'block';
                        individualAmount.textContent = this.formatCurrency(individualMarkupAmount);
                        totalWithMarkupElement.textContent = this.formatCurrency(totalWithIndividualMarkup);
                    } else {
                        individualInfo.style.display = 'none';
                    }
                }

                // Hidden markup info
                const hiddenInfo = document.getElementById('hidden-markup-info');
                const hiddenAmount = document.getElementById('hidden-markup-amount');
                if (hiddenInfo && hiddenAmount) {
                    if (this.state.hiddenMarkup > 0) {
                        hiddenInfo.style.display = 'block';
                        hiddenAmount.textContent = this.formatCurrency(hiddenMarkupAmount);
                    } else {
                        hiddenInfo.style.display = 'none';
                    }
                }

                // Update grand total (показываем клиенту сумму БЕЗ скрытой маржи)
                const finalTotal = document.getElementById('grand-total');
                if (finalTotal) {
                    finalTotal.textContent = this.formatCurrency(clientTotal);
                }
                
                // Update profit analysis
                const totalProfitElement = document.getElementById('total-profit');
                if (totalProfitElement) {
                    totalProfitElement.textContent = this.formatCurrency(totalProfit);
                }

                // Partner commission display - теперь внутри блока "Наша прибыль"
                const partnerCommissionRow = document.getElementById('partner-commission-row');
                const partnerCommissionAmountElement = document.getElementById('partner-commission-amount');

                if (partnerCommissionRow && partnerCommissionAmountElement) {
                    if (this.state.partnerCommission > 0) {
                        partnerCommissionRow.style.display = 'block';
                        partnerCommissionAmountElement.textContent = this.formatCurrency(partnerCommissionAmount);
                    } else {
                        partnerCommissionRow.style.display = 'none';
                    }
                }

                // Phase 8a: обновляем блок «Статус заказов» в правой панели
                if (typeof this.updateChecklistStats === 'function') {
                    this.updateChecklistStats();
                }

                // Update accommodations if section is expanded
                // Save to localStorage automatically
                this.saveToLocalStorage();
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: CATALOG MODALS (extracted Phase 5)
            //   📦 showCatalogModal, updateCatalogModalCategories,
            //      updateCatalogDayVisibility, hideCatalogModal,
            //      editTemplate, duplicateTemplate, duplicateCurrentTemplate,
            //      deleteTemplate, addToCatalog
            //   → public/js/app/catalog-modals.js
            // ════════════════════════════════════════════════════════════════════

            formatCurrency(amount) {
                return `${this.config.currency}${Math.round(amount).toLocaleString(this.config.locale)}`;
            }

            filterTemplates(query) {
                const items = document.querySelectorAll('.template-item');
                const searchQuery = query.toLowerCase();

                items.forEach(item => {
                    // Поиск по названию, контрагенту и комментариям
                    const name = item.getAttribute('data-name') || '';
                    const contractor = item.getAttribute('data-contractor') || '';
                    const description = item.getAttribute('data-description') || '';

                    const matches = name.includes(searchQuery) ||
                                   contractor.includes(searchQuery) ||
                                   description.includes(searchQuery);

                    item.style.display = matches ? 'block' : 'none';

                    // Highlight search terms
                    if (query && matches) {
                        item.style.backgroundColor = 'var(--gray-100)';
                    } else {
                        item.style.backgroundColor = '';
                    }
                });
            }

            // Category Management
            loadCategories() {
                const saved = localStorage.getItem('quoteCalc_categories');
                if (saved) {
                    this.categories = JSON.parse(saved);
                    console.log('Loaded categories:', this.categories.map(c => `${c.name}(${c.id})`).join(', '));
                }

                // Исправляем или удаляем поломанные категории (без ID или с пустым ID)
                const beforeCleanup = this.categories.length;
                const brokenCategories = this.categories.filter(cat => !cat.id || cat.id === '');

                if (brokenCategories.length > 0) {
                    console.warn('Found broken categories:', brokenCategories);

                    // Пытаемся исправить категории с пустым ID
                    brokenCategories.forEach(cat => {
                        if (cat.name) {
                            // Генерируем новый ID
                            let newId = cat.name.toLowerCase()
                                .replace(/\s+/g, '_')
                                .replace(/[^a-z0-9_]/g, '');

                            if (!newId || newId === '') {
                                newId = 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                            }

                            console.log(`Fixing category "${cat.name}": setting id to "${newId}"`);
                            cat.id = newId;
                        }
                    });

                    // Удаляем те, которые все еще без ID
                    this.categories = this.categories.filter(cat => cat.id && cat.id !== '');

                    if (this.categories.length < beforeCleanup) {
                        console.log(`Удалено ${beforeCleanup - this.categories.length} поломанных категорий`);
                    }

                    this.saveCategories();
                }

                // Убедимся, что системная категория "Не определено" существует
                const hasUndefinedCategory = this.categories.some(cat => cat.id === 'undefined');
                if (!hasUndefinedCategory) {
                    // Добавляем системную категорию в начало списка
                    this.categories.unshift({
                        id: 'undefined',
                        name: 'Не определено',
                        icon: '❓',
                        system: true
                    });
                    this.saveCategories(); // Сохраняем обновленный список
                    console.log('Системная категория "Не определено" добавлена');
                }

                // Load category statistics
                const savedStats = localStorage.getItem('quoteCalc_categoryStats');
                if (savedStats) {
                    this.categoryStats = JSON.parse(savedStats);
                } else {
                    // Initialize usage stats for all categories
                    this.categories.forEach(cat => {
                        if (!this.categoryStats.usage[cat.id]) {
                            this.categoryStats.usage[cat.id] = 0;
                        }
                    });
                }

                // Check if reorganization is needed
                this.checkCategoryReorganization();

                // Load template statistics
                const savedTemplateStats = localStorage.getItem('quoteCalc_templateStats');
                if (savedTemplateStats) {
                    this.templateStats = JSON.parse(savedTemplateStats);
                } else {
                    // Initialize usage stats for all templates
                    this.templates.forEach(tpl => {
                        if (!this.templateStats.usage[tpl.id]) {
                            this.templateStats.usage[tpl.id] = 0;
                        }
                    });
                }

                // Check if template reorganization is needed
                this.checkTemplateReorganization();

                // Загрузка глобальной статистики
                this.loadGlobalStats();
            }

            saveCategories() {
                // ✅ Сохраняем категории через сервер (не localStorage)
                // Каталог сохраняется целиком (templates + categories)
                this.saveCatalogToRegion(this.currentRegion).catch(err => {
                    console.error('Failed to save categories:', err);
                });
            }

            saveCategoryStats() {
                // Статистика категорий общая для всех регионов
                localStorage.setItem('quoteCalc_categoryStats', JSON.stringify(this.categoryStats));
            }

            saveTemplateStats() {
                // Статистика услуг общая для всех регионов
                localStorage.setItem('quoteCalc_templateStats', JSON.stringify(this.templateStats));
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: REGIONS (tour regions Argentina/Chile/Antarctica/...)
            //   📦 Извлечено в public/js/app/regions.js (Sprint 15 C.3):
            //     - initRegions / renderRegionSelector / renderRegionsManager
            //     - selectRegionForView / loadCategoriesForRegionView
            //     - startInlineRename / finishInlineRename
            //     - addRegionFromManager / deleteRegion / changeRegion
            // ════════════════════════════════════════════════════════════════════

            async saveCatalogToRegion(regionName) {
                try {
                    // Prepare catalog data
                    const catalogData = {
                        name: regionName,
                        data: {
                            version: this.CATALOG_VERSION,
                            region: regionName,
                            templates: this.templates,
                            categories: this.categories,
                            updated_at: new Date().toISOString()
                        },
                        visibility: 'organization'  // or 'public'/'private' based on UI
                    };

                    // Save to server
                    const response = await this.apiClient.saveCatalog(
                        catalogData.name,
                        catalogData.data,
                        catalogData.visibility
                    );

                    if (!response.success) {
                        throw new Error(response.error || 'Save failed');
                    }

                    // Success feedback
                    this.showNotification(`${_t('app.toast.catalog_saved_prefix', 'Catalog')} "${regionName}" ${_t('app.toast.saved_suffix', 'saved')}`, false);
                    console.log(`Каталог "${regionName}" сохранён на сервере`);

                } catch (error) {
                    console.error('Save catalog error:', error);

                    // Обработка специфичных ошибок
                    if (error.message.includes('Authentication required')) {
                        this.showNotification(_t('app.toast.session_expired', 'Session expired. Please sign in again.'), true);
                    } else if (error.message.includes('Concurrent modification')) {
                        this.showNotification(_t('app.toast.catalog_concurrent_change', 'Catalog was modified by another user. Refresh and try again.'), true);
                    } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                        // Migration v3: Queue for offline sync
                        if (window.offlineManager) {
                            window.offlineManager.queueSave(catalogData);
                            this.showNotification(_t('app.toast.server_offline_queued', 'Server unavailable. Changes will sync when you reconnect.'), false);
                        } else {
                            this.showNotification(_t('app.toast.server_offline_check', 'Server unavailable. Check your connection.'), true);
                        }
                    } else {
                        this.showNotification(`${_t('app.error.catalog_save', 'Failed to save catalog')}: ${error.message}`, true);
                    }
                }
            }

            async loadCatalogForRegion(regionName) {
                // Guard flag для предотвращения concurrent operations
                if (this.isLoadingCatalog) {
                    console.warn('Catalog load already in progress');
                    return;
                }

                this.isLoadingCatalog = true;

                try {
                    // Step 1: Get list of catalogs from server
                    const listResponse = await this.apiClient.getCatalogsList();
                    if (!listResponse.success) {
                        throw new Error('Failed to fetch catalogs list');
                    }

                    // Step 2: Find catalog matching regionName
                    const catalogs = listResponse.data.catalogs;
                    const catalog = catalogs.find(c => c.region === regionName);

                    if (!catalog) {
                        // Region doesn't exist yet - создаем пустой
                        console.log(`Каталог для региона "${regionName}" не найден, создаем пустой`);
                        this.templates = [];
                        this.categories = this.getDefaultCategories();
                        this.currentCatalogId = null;
                    } else {
                        // Step 3: Load full catalog data by ID
                        const dataResponse = await this.apiClient.loadCatalogById(catalog.id);
                        if (!dataResponse.success) {
                            throw new Error('Failed to load catalog data');
                        }

                        // Step 4: Populate application state
                        const data = dataResponse.data;
                        this.templates = (data.templates || []).map(t => ({
                            ...t,
                            region: t.region || regionName
                        }));
                        this.categories = data.categories || this.getDefaultCategories();
                        this.currentCatalogId = catalog.id;  // Store for future saves

                        // Migration v3: Cache успешно загруженных данных
                        if (window.catalogCache) {
                            window.catalogCache.set(catalog.id, {
                                templates: this.templates,
                                categories: this.categories,
                                region: regionName
                            });
                        }

                        console.log(`Загружен каталог "${regionName}" (ID: ${catalog.id}) с сервера: ${this.templates.length} услуг`);
                    }

                    // ВАЖНО: Убеждаемся что системная категория "Не определено" ВСЕГДА существует
                    const hasUndefinedCategory = this.categories.some(cat => cat.id === 'undefined');
                    if (!hasUndefinedCategory) {
                        console.log(`Добавляем системную категорию "Не определено" в регион "${regionName}"`);
                        this.categories.unshift({
                            id: 'undefined',
                            name: 'Не определено',
                            icon: '❓',
                            system: true
                        });
                    }

                    // Загружаем статистику категорий (общая для всех регионов)
                    const savedStats = localStorage.getItem('quoteCalc_categoryStats');
                    if (savedStats) {
                        this.categoryStats = JSON.parse(savedStats);
                    } else {
                        // Инициализируем статистику для всех категорий
                        this.categories.forEach(cat => {
                            if (!this.categoryStats.usage[cat.id]) {
                                this.categoryStats.usage[cat.id] = 0;
                            }
                        });
                    }

                    // Загружаем статистику услуг (общая для всех регионов)
                    const savedTemplateStats = localStorage.getItem('quoteCalc_templateStats');
                    if (savedTemplateStats) {
                        this.templateStats = JSON.parse(savedTemplateStats);
                    } else {
                        // Инициализируем статистику для всех услуг
                        this.templates.forEach(tpl => {
                            if (!this.templateStats.usage[tpl.id]) {
                                this.templateStats.usage[tpl.id] = 0;
                            }
                        });
                    }

                    // Проверяем нужна ли реорганизация
                    this.checkCategoryReorganization();
                    this.checkTemplateReorganization();

                    // Загружаем глобальную статистику (при первой загрузке)
                    if (!this.globalStats) {
                        this.loadGlobalStats();
                    }

                    // Перерисовываем UI
                    this.renderTemplates();
                    this.renderCategories();
                    this.renderCategoryOptions();
                    this.renderRegionsManager();

                    // Показываем уведомление об успешной загрузке
                    this.showNotification(`${_t('app.toast.catalog_loaded_prefix', 'Catalog')} "${regionName}" ${_t('app.toast.loaded_suffix', 'loaded')}`, false);

                } catch (error) {
                    console.error('Load catalog error:', error);

                    // Migration v3: Check cache before fallback
                    let usedCache = false;
                    if (window.catalogCache && this.currentCatalogId) {
                        const cached = window.catalogCache.get(this.currentCatalogId);
                        if (cached) {
                            this.templates = cached.templates;
                            this.categories = cached.categories;
                            this.showNotification(_t('app.toast.loaded_from_cache', 'Loaded from cache (offline)'), false);
                            usedCache = true;

                            // Render UI with cached data
                            this.renderTemplates();
                            this.renderCategories();
                            return;
                        }
                    }

                    // Обработка специфичных ошибок (если кэш не помог)
                    if (error.message.includes('Authentication required')) {
                        this.showNotification(_t('app.toast.session_expired', 'Session expired. Please sign in again.'), true);
                        try {
                            localStorage.removeItem('jwt_token');
                            localStorage.removeItem('auth_user');
                        } catch (_) { /* localStorage may be unavailable */ }
                        // Небольшая задержка, чтобы пользователь увидел нотификацию
                        setTimeout(() => {
                            const next = encodeURIComponent(window.location.pathname + window.location.search);
                            window.location.href = `/login.html?next=${next}`;
                        }, 800);
                    } else if (error.message.includes('Catalog not found') || error.message.includes('not found')) {
                        this.showNotification(`${_t('app.toast.catalog_loaded_prefix', 'Catalog')} "${regionName}" ${_t('app.toast.catalog_not_found_create', 'not found. Create a new one.')}`, true);
                    } else if (error.message.includes('Access denied')) {
                        this.showNotification(_t('app.toast.catalog_access_denied', 'Access denied to this catalog.'), true);
                    } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                        this.showNotification(_t('app.toast.server_offline_check', 'Server unavailable. Check your connection.'), true);
                    } else {
                        this.showNotification(`${_t('app.error.catalog_load', 'Failed to load catalog')}: ${error.message}`, true);
                    }

                    // Fallback: используем пустой каталог (только если не использовали кэш)
                    if (!usedCache) {
                        this.templates = [];
                        this.categories = this.getDefaultCategories();
                        this.renderTemplates();
                        this.renderCategories();
                    }

                } finally {
                    this.isLoadingCatalog = false;
                }
            }

            async loadDefaultCatalog() {
                // Sprint 18 fix: initRegions() уже инициирует загрузку каталога
                // (loadCatalogForRegion(currentRegion)) синхронно из init().
                // К моменту вызова loadDefaultCatalog (init.js:207) — либо
                // загрузка идёт (isLoadingCatalog=true), либо currentRegion
                // уже установлен. Дублирующий вызов выдавал warning
                // "Catalog load already in progress" в консоли.
                if (this.currentRegion || this.isLoadingCatalog) {
                    console.log('[loadDefaultCatalog] Skip — initRegions уже обрабатывает');
                    return;
                }
                try {
                    // Option A: Load first catalog from list
                    const listResponse = await this.apiClient.getCatalogsList();
                    if (listResponse.success && listResponse.data.catalogs.length > 0) {
                        const firstCatalog = listResponse.data.catalogs[0];
                        await this.loadCatalogForRegion(firstCatalog.region);
                        console.log(`Default catalog loaded: ${firstCatalog.region}`);
                        return;
                    }

                    // Fallback: Empty catalog
                    console.log('No catalogs found, starting with empty catalog');
                    this.templates = [];
                    this.categories = this.getDefaultCategories();

                } catch (error) {
                    // Non-blocking error - app still usable
                    console.warn('Default catalog not loaded:', error);
                    this.showNotification(_t('app.toast.catalog_not_loaded_manual', 'Catalog not loaded. Load manually.'), false);

                    // Fallback: Empty catalog
                    this.templates = [];
                    this.categories = this.getDefaultCategories();
                }
            }

            getDefaultCategories() {
                return [
                    { id: 'undefined', name: 'Не определено', icon: '❓', system: true },
                    { id: 'transfer', name: 'Трансфер', icon: '🚗' },
                    { id: 'accommodation', name: 'Отели', icon: '🏨' },
                    { id: 'guide', name: 'Гиды', icon: '👨‍🏫' },
                    { id: 'activity', name: 'Активности', icon: '🏛️' },
                    { id: 'service', name: 'Сервис', icon: '🔧' }
                ];
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: STATS & TRACKING (extracted Phase 5)
            //   📦 escapeHtml, validateAndFixTemplateCategories,
            //      Global stats (load/init/save/trackGlobalUsage),
            //      Local stats (trackCategoryUsage/trackTemplateUsage),
            //      Reorganization by popularity (check + reorganize)
            //   → public/js/app/usage-stats.js
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: RENDER CATEGORIES (extracted Phase 5)
            //   📦 renderCategories, toggleSearch, toggleSearchExpand, toggleCategories
            //   → public/js/app/render-categories.js
            // ════════════════════════════════════════════════════════════════════

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: FILE MENU & CATEGORIES MANAGER (extracted Phase 5)
            //   📦 toggleFileMenu, showCategoriesManager + manager modal,
            //      drag-drop, categories CRUD, folder picker
            //   → public/js/app/file-menu-categories.js
            // ════════════════════════════════════════════════════════════════════

            async saveCatalogJSON() {
                try {
                    console.log('saveCatalogJSON called, catalogDirectoryHandle:', !!this.catalogDirectoryHandle);

                    // Собираем каталоги всех регионов
                    const allRegions = {};
                    let totalServices = 0;
                    let totalCategories = 0;

                    for (const region of this.regions) {
                        const templatesKey = `quoteCalc_templates_${region}`;
                        const categoriesKey = `quoteCalc_categories_${region}`;

                        const templates = JSON.parse(localStorage.getItem(templatesKey) || '[]');
                        const categories = JSON.parse(localStorage.getItem(categoriesKey) || '[]');

                        allRegions[region] = {
                            templates: templates,
                            categories: categories
                        };

                        totalServices += templates.length;
                        totalCategories += categories.length;
                    }

                    const catalogData = {
                        version: this.CATALOG_VERSION,
                        appVersion: this.APP_VERSION,
                        timestamp: new Date().toISOString(),
                        currentRegion: this.currentRegion,
                        regions: this.regions,
                        regionData: allRegions,
                        metadata: {
                            totalRegions: this.regions.length,
                            totalServices: totalServices,
                            totalCategories: totalCategories,
                            createdBy: 'Quote Calculator v' + this.APP_VERSION
                        }
                    };

                    const jsonString = JSON.stringify(catalogData, null, 2);
                    const filename = 'catalog.json';

                    // Если выбрана папка, сохраняем туда
                    if (this.catalogDirectoryHandle) {
                        console.log('Saving to selected folder:', this.state.saveFolder);
                        await this.saveFileToSelectedFolder(filename, jsonString);
                        this.showNotification(`${_t('app.toast.catalog_saved_with_stats', 'Catalog saved')} (${this.regions.length} ${_t('app.unit.regions', 'regions')}, ${totalServices} ${_t('app.unit.services', 'services')})`);

                        // Автоматически создаём бэкап
                        await this.createBackup();
                    } else {
                        console.log('No folder selected, saving to downloads');
                        // Иначе используем стандартную загрузку
                        this.downloadJSON(jsonString, filename);
                        this.showNotification(`${_t('app.toast.catalog_saved_with_stats', 'Catalog saved')} (${this.regions.length} ${_t('app.unit.regions', 'regions')}, ${totalServices} ${_t('app.unit.services', 'services')})`);
                    }
                } catch (error) {
                    this.showNotification(`${_t('app.error.catalog_save', 'Failed to save catalog')}: ` + error.message, true);
                }
            }

            async saveQuoteJSON() {
                try {
                    const quoteData = {
                        version: this.QUOTE_VERSION,
                        appVersion: this.APP_VERSION,
                        timestamp: new Date().toISOString(),
                        metadata: {
                            paxCount: this.state.paxCount,
                            tourStart: this.state.tourStart,
                            tourEnd: this.state.tourEnd,
                            clientName: this.state.clientName,
                            clientPhone: this.state.clientPhone,
                            clientEmail: this.state.clientEmail,
                            quoteComments: this.state.quoteComments,
                            programDescription: this.state.programDescription,
                            accommodations: this.state.accommodations,
                            bookingTerms: this.state.bookingTerms,
                            hiddenMarkup: this.state.hiddenMarkup,
                            partnerCommission: this.state.partnerCommission
                        },
                        services: this.state.services,
                        hotels: this.state.hotels || [],
                        flights: this.state.flights || [],
                        otherServices: this.state.otherServices || [],
                        optionalServices: this.state.optionalServices || [],
                        calculations: {
                            baseCost: this.calculateBaseCost(),
                            totalProfit: this.calculateTotalProfit(),
                            clientTotal: this.calculateClientTotal()
                        }
                    };

                    const jsonString = JSON.stringify(quoteData, null, 2);

                    // Если есть fileHandle - перезаписываем существующий файл
                    if (this.currentFileHandle && 'createWritable' in FileSystemFileHandle.prototype) {
                        try {
                            const writable = await this.currentFileHandle.createWritable();
                            await writable.write(jsonString);
                            await writable.close();

                            this.state.isQuoteSaved = true;
                            this.updateQuoteStatusBar();

                            // Сохраняем последнюю смету для автозагрузки
                            localStorage.setItem('quoteCalc_lastQuote', jsonString);
                            localStorage.setItem('quoteCalc_lastQuoteFileName', this.state.currentQuoteFile);

                            // Очищаем автосохранение (смета теперь сохранена в файл)
                            localStorage.removeItem('quoteCalc_autosave');
                            localStorage.removeItem('quoteCalc_autosave_timestamp');

                            this.showNotification(`${_t('app.toast.quote_saved', 'Quote saved')}: ${this.state.currentQuoteFile}`);
                            return;
                        } catch (error) {
                            console.warn('Не удалось перезаписать файл:', error);
                            // Fallback на обычное сохранение
                        }
                    }

                    // Улучшенный нейминг: ФИО заказчика + дата + количество человек
                    let filename = '';

                    if (this.state.clientName) {
                        filename += this.state.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s]/g, '').replace(/\s+/g, '_');
                    } else {
                        filename += _t('app.filename.default_quote', 'Quote');
                    }

                    if (this.state.tourStart) {
                        filename += `_${this.state.tourStart}`;
                    } else {
                        const today = new Date().toISOString().split('T')[0];
                        filename += `_${today}`;
                    }

                    if (this.state.paxCount && this.state.paxCount > 0) {
                        filename += `_${this.state.paxCount}pax`;
                    }

                    filename += '.json';

                    // Пытаемся сохранить в выбранную папку
                    const savedToFolder = await this.saveToSelectedFolder(jsonString, filename);

                    if (!savedToFolder) {
                        // Если не удалось сохранить в выбранную папку, используем обычное скачивание
                        this.downloadJSON(jsonString, filename);

                        // Обновляем статус даже при обычном скачивании
                        this.state.currentQuoteFile = filename;
                        this.state.isQuoteSaved = true;
                        this.updateQuoteStatusBar();

                        // Очищаем автосохранение (смета теперь сохранена в файл)
                        localStorage.removeItem('quoteCalc_autosave');
                        localStorage.removeItem('quoteCalc_autosave_timestamp');

                        if (this.state.saveFolder) {
                            this.showNotification(`${_t('app.toast.file_downloaded_move', 'File downloaded. Move to folder')}: ${this.state.saveFolder}`);
                        } else {
                            this.showNotification(_t('app.toast.quote_saved_json', 'Quote saved as JSON'));
                        }
                    } else {
                        // Очищаем автосохранение (смета теперь сохранена в файл)
                        localStorage.removeItem('quoteCalc_autosave');
                        localStorage.removeItem('quoteCalc_autosave_timestamp');

                        this.showNotification(`${_t('app.toast.quote_saved_folder', 'Quote saved to folder')}: ${this.state.saveFolder}`);
                    }
                } catch (error) {
                    this.showNotification(`${_t('app.error.quote_save', 'Failed to save quote')}: ` + error.message, true);
                }
            }

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: CALCULATIONS — TOTALS
            //   📦 Извлечено в public/js/app/calculations.js (Phase 5).
            //   calculateBaseCost / calculateTotalProfit / calculateClientTotal
            //   добавляются на prototype через attachCalculations() из entry.js.
            // ════════════════════════════════════════════════════════════════════

            // Автосохранение сметы в localStorage

            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: AUTOSAVE & PERSISTENCE
            //   📦 Извлечено в public/js/app/autosave.js (Phase 5):
            //     - autoSaveQuote (legacy localStorage)
            //     - prepareEstimateData (FLAT payload)
            //     - saveToLocalStorage (server autosave с debounce 8с)
            // ════════════════════════════════════════════════════════════════════

            // Download JSON helper
            downloadJSON(jsonString, filename) {
                try {
                    const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    this.showNotification(`${_t('app.error.file_download', 'Failed to download file')}: ` + error.message, true);
                }
            }

            // JSON Load functions
            loadCatalogJSON(event) {
                const file = event.target.files[0];
                if (!file) return;

                // File size validation
                if (file.size > this.config.maxFileSize) {
                    this.showNotification(`${_t('app.error.file_too_large', 'File too large')} (${(file.size/1024/1024).toFixed(1)}MB). ${_t('app.error.max_size_5mb', 'Maximum 5MB')}`, true);
                    event.target.value = '';
                    return;
                }

                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const jsonData = JSON.parse(e.target.result);

                        // Version validation
                        if (!jsonData.version) {
                            this.showNotification(_t('app.error.file_no_version', 'File does not contain version information'), true);
                            return;
                        }

                        if (jsonData.version > this.CATALOG_VERSION) {
                            this.showNotification(`${_t('app.error.file_newer_version', 'File was created in a newer version')} (${jsonData.version}). ${_t('app.error.current_version', 'Current version')}: ${this.CATALOG_VERSION}`, true);
                            return;
                        }

                        // Проверяем формат файла
                        const isMultiRegionFormat = jsonData.regionData && jsonData.regions;

                        if (isMultiRegionFormat) {
                            // НОВЫЙ ФОРМАТ: несколько регионов
                            const totalServices = jsonData.metadata?.totalServices || 0;
                            const message = `${_t('app.confirm.catalog_load_replace_all', 'Loading this catalog will replace all existing data.')}\n\n${_t('app.confirm.file_contains', 'File contains')}:\n- ${jsonData.regions.length} ${_t('app.unit.regions', 'regions')}\n- ${totalServices} ${_t('app.unit.services', 'services')}\n\n${_t('app.confirm.backup_recommended', 'We recommend creating a backup.')}\n\n${_t('app.confirm.continue', 'Continue?')}`;

                            const okMultiRegion = await confirmModal({
                                title: _t('app.confirm.catalog_load_title', 'Load catalog'),
                                message,
                                danger: true,
                                confirmLabel: _t('btn.continue', 'Continue'),
                                cancelLabel: _t('btn.cancel', 'Cancel')
                            });
                            if (!okMultiRegion) {
                                event.target.value = '';
                                return;
                            }

                            // Загружаем регионы
                            this.regions = jsonData.regions;
                            localStorage.setItem(`quoteCalc_regions_${((window.__crmUser__ && window.__crmUser__.organization_id) || 'unknown')}`, JSON.stringify(this.regions));

                            // Загружаем каталоги всех регионов
                            for (const region of this.regions) {
                                const regionData = jsonData.regionData[region];
                                if (regionData) {
                                    const templatesKey = `quoteCalc_templates_${region}`;
                                    const categoriesKey = `quoteCalc_categories_${region}`;

                                    // Добавляем region к каждому услугу
                                    const templatesWithRegion = (regionData.templates || []).map(t => ({
                                        ...t,
                                        region: t.region || region // Используем region из услуги или имя региона
                                    }));

                                    localStorage.setItem(templatesKey, JSON.stringify(templatesWithRegion));
                                    localStorage.setItem(categoriesKey, JSON.stringify(regionData.categories || []));
                                }
                            }

                            // Переключаемся на регион из файла (или первый)
                            const targetRegion = jsonData.currentRegion || this.regions[0];
                            this.currentRegion = targetRegion;
                            localStorage.setItem(`quoteCalc_currentRegion_${((window.__crmUser__ && window.__crmUser__.organization_id) || 'unknown')}`, this.currentRegion);

                            // Загружаем каталог текущего региона
                            this.loadCatalogForRegion(this.currentRegion);

                            this.showNotification(`${_t('app.toast.catalog_loaded_summary', 'Catalog loaded')}: ${jsonData.regions.length} ${_t('app.unit.regions', 'regions')}, ${totalServices} ${_t('app.unit.services', 'services')}`);

                        } else {
                            // СТАРЫЙ ФОРМАТ: один регион (обратная совместимость)
                            const message = `${_t('app.confirm.catalog_load_replace_region_prefix', 'Loading a new catalog will replace services in the current region')} "${this.currentRegion}".\n\n${_t('app.confirm.backup_recommended', 'We recommend creating a backup.')}\n\n${_t('app.confirm.continue', 'Continue?')}`;
                            const okSingleRegion = await confirmModal({
                                title: _t('app.confirm.catalog_load_title', 'Load catalog'),
                                message,
                                danger: true,
                                confirmLabel: _t('btn.continue', 'Continue'),
                                cancelLabel: _t('btn.cancel', 'Cancel')
                            });
                            if (!okSingleRegion) {
                                event.target.value = '';
                                return;
                            }

                            // Применяем к текущему региону
                            if (jsonData.categories) {
                                this.categories = jsonData.categories;
                                this.saveCategories();
                            }

                            if (jsonData.templates) {
                                // Добавляем region к каждому услугу
                                this.templates = jsonData.templates.map(t => ({
                                    ...t,
                                    region: t.region || this.currentRegion
                                }));
                                this.saveToLocalStorage();
                            }

                            // Проверяем и исправляем категории
                            this.validateAndFixTemplateCategories();

                            // Обновляем UI
                            this.renderCategories();
                            this.renderCategoryOptions();
                            this.renderTemplates();
                            this.renderRegionsManager();

                            this.showNotification(`${_t('app.toast.catalog_loaded_into_region', 'Catalog loaded into region')} "${this.currentRegion}": ${jsonData.templates?.length || 0} ${_t('app.unit.services', 'services')}`);
                        }

                    } catch (error) {
                        this.showNotification(`${_t('app.error.json_load', 'Failed to load JSON')}: ` + error.message, true);
                    }
                };
                reader.readAsText(file, 'UTF-8');
            }

            loadQuoteJSON(event) {
                const file = event.target.files[0];
                if (!file) return;

                // File size validation
                if (file.size > this.config.maxFileSize) {
                    this.showNotification(`${_t('app.error.file_too_large', 'File too large')} (${(file.size/1024/1024).toFixed(1)}MB). ${_t('app.error.max_size_5mb', 'Maximum 5MB')}`, true);
                    event.target.value = '';
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const quoteData = JSON.parse(e.target.result);

                        // Version validation
                        if (!quoteData.version) {
                            this.showNotification(_t('app.error.file_no_version', 'File does not contain version information'), true);
                            return;
                        }

                        if (quoteData.version > this.QUOTE_VERSION) {
                            this.showNotification(`${_t('app.error.file_newer_version', 'File was created in a newer version')} (${quoteData.version}). ${_t('app.error.current_version', 'Current version')}: ${this.QUOTE_VERSION}`, true);
                            return;
                        }

                        // Apply metadata (с миграцией старых полей)
                        if (quoteData.metadata) {
                            // Копируем metadata с переименованием quoteDescription → quoteComments
                            const metadata = { ...quoteData.metadata };
                            // Миграция: если есть старое поле quoteDescription, переименуем в quoteComments
                            if ('quoteDescription' in metadata && !('quoteComments' in metadata)) {
                                metadata.quoteComments = metadata.quoteDescription;
                                delete metadata.quoteDescription;
                            }
                            Object.assign(this.state, metadata);
                        }

                        // Apply services с обеспечением совместимости
                        if (quoteData.services) {
                            this.state.services = quoteData.services.map(service => ({
                                ...service,
                                excludeFromMarkup: service.excludeFromMarkup || false,
                                fullProfit: service.fullProfit || false,
                                region: service.region || this.currentRegion // Для старых файлов без региона
                            }));
                            this.state.selectedServices = new Set();
                        }

                        // Apply optional services
                        if (quoteData.optionalServices) {
                            this.state.optionalServices = quoteData.optionalServices.map(service => ({
                                ...service,
                                excludeFromMarkup: service.excludeFromMarkup || false,
                                fullProfit: service.fullProfit || false,
                                region: service.region || this.currentRegion // Для старых файлов без региона
                            }));
                        } else {
                            this.state.optionalServices = [];
                        }

                        // Apply hotels
                        if (quoteData.hotels) {
                            this.state.hotels = quoteData.hotels.map(service => ({
                                ...service,
                                excludeFromMarkup: service.excludeFromMarkup || false,
                                fullProfit: service.fullProfit || false,
                                region: service.region || this.currentRegion
                            }));
                        } else {
                            this.state.hotels = [];
                        }

                        // Apply flights
                        if (quoteData.flights) {
                            this.state.flights = quoteData.flights.map(service => ({
                                ...service,
                                excludeFromMarkup: service.excludeFromMarkup || false,
                                fullProfit: service.fullProfit || false,
                                region: service.region || this.currentRegion
                            }));
                        } else {
                            this.state.flights = [];
                        }

                        // Apply other services
                        if (quoteData.otherServices) {
                            this.state.otherServices = quoteData.otherServices.map(service => ({
                                ...service,
                                excludeFromMarkup: service.excludeFromMarkup || false,
                                fullProfit: service.fullProfit || false,
                                region: service.region || this.currentRegion
                            }));
                        } else {
                            this.state.otherServices = [];
                        }

                        this.loadStateToUI();
                        this.renderServices();
                        this.renderOptionalServices();
                        this.renderHotels();
                        this.renderFlights();
                        this.renderOtherServices();
                        this.updateCalculations();

                        // Обновляем статус файла
                        this.state.currentQuoteFile = file.name;
                        this.state.isQuoteSaved = true;
                        this.updateQuoteStatusBar();
                        this.updateFiltersVisibility();

                        this.showNotification(`${_t('app.toast.quote_loaded_version', 'Quote loaded')} (${_t('app.unit.version', 'version')} ${quoteData.version}): ${quoteData.services?.length || 0} ${_t('app.unit.services', 'services')}`);

                    } catch (error) {
                        this.showNotification(`${_t('app.error.json_load', 'Failed to load JSON')}: ` + error.message, true);
                    }
                };
                reader.readAsText(file, 'UTF-8');
            }

            // Universal file load functions
            loadCatalogFile(event) {
                const file = event.target.files[0];
                if (!file) return;
                
                const extension = file.name.split('.').pop().toLowerCase();
                
                if (extension === 'json') {
                    this.loadCatalogJSON(event);
                } else if (extension === 'csv') {
                    this.loadCatalogCSV(event);
                } else {
                    this.showNotification(_t('app.error.unsupported_format', 'Only .json and .csv files are supported'), true);
                    event.target.value = '';
                }
            }

            loadQuoteFile(event) {
                const file = event.target.files[0];
                if (!file) return;
                
                const extension = file.name.split('.').pop().toLowerCase();
                
                if (extension === 'json') {
                    this.loadQuoteJSON(event);
                } else if (extension === 'csv') {
                    this.loadQuoteCSV(event);
                } else {
                    this.showNotification(_t('app.error.unsupported_format', 'Only .json and .csv files are supported'), true);
                    event.target.value = '';
                }
            }

            // ===== ENHANCED CSV FUNCTIONS WITH FILE SIZE VALIDATION =====

            async saveCatalogCSV() {
                try {
                    // Предлагаем создать бэкап перед перезаписью
                    if (this.templates.length > 0) {
                        const wantBackup = await confirmModal({
                            title: _t('app.confirm.backup_before_save_title', 'Create backup?'),
                            message: _t('app.confirm.backup_before_save_msg', 'Create a backup of the catalog before saving?'),
                            confirmLabel: _t('btn.create', 'Create'),
                            cancelLabel: _t('btn.skip', 'Skip')
                        });
                        if (wantBackup) {
                            this.createBackup();
                        }
                    }
                    
                    const headers = ['type', 'id', 'name', 'category', 'description', 'contractor', 'icon', 'price'];
                    const rows = [];
                    
                    // Сначала сохранить категории
                    this.categories.forEach(cat => {
                        rows.push([
                            'category',
                            this.escapeCSVField(cat.id),
                            this.escapeCSVField(cat.name),
                            '', // category empty for categories themselves
                            '', // description empty for categories
                            '', // contractor empty for categories  
                            this.escapeCSVField(cat.icon),
                            '' // price empty for categories
                        ]);
                    });
                    
                    // Затем сохранить услуги
                    this.templates.forEach(t => {
                        rows.push([
                            'service',
                            this.escapeCSVField(t.id),
                            this.escapeCSVField(t.name),
                            this.escapeCSVField(t.category),
                            this.escapeCSVField(t.description || ''),
                            this.escapeCSVField(t.contractor || ''),
                            this.escapeCSVField(t.icon),
                            this.escapeCSVField(t.price)
                        ]);
                    });

                    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
                    this.downloadCSV(csv, 'catalog.csv');
                    this.showNotification(_t('app.toast.catalog_saved', 'Catalog saved'));
                } catch (error) {
                    this.showNotification(`${_t('app.error.catalog_save', 'Failed to save catalog')}: ` + error.message, true);
                }
            }

            async createBackup() {
                try {
                    if (this.templates.length === 0) {
                        // Тихо выходим, если каталог пуст (автоматический бэкап)
                        return;
                    }

                    // Сохраняем в JSON формате (более надёжный чем CSV)
                    const backupData = {
                        version: this.CATALOG_VERSION,
                        exportDate: new Date().toISOString(),
                        categories: this.categories,
                        templates: this.templates,
                        categoryStats: this.categoryStats,
                        globalStats: this.globalStats
                    };

                    // Фиксированное имя файла для перезаписи
                    const backupFilename = 'catalog_backup.json';
                    const jsonStr = JSON.stringify(backupData, null, 2);

                    // Если выбрана папка, сохраняем туда
                    if (this.catalogDirectoryHandle) {
                        await this.saveFileToSelectedFolder(backupFilename, jsonStr);
                        console.log('Автоматический бэкап создан в папке:', this.state.saveFolder);
                    } else {
                        // Иначе используем стандартную загрузку
                        this.downloadJSON(jsonStr, backupFilename);
                        console.log('Автоматический бэкап создан:', backupFilename);
                    }
                } catch (error) {
                    console.error('Ошибка создания автобэкапа:', error.message);
                }
            }

            // ===== IMPORT/EXPORT ALL DATA =====

            async handleExportAll() {
                try {
                    // Собираем все данные из localStorage и БД
                    const exportData = {
                        version: this.APP_VERSION,
                        exportDate: new Date().toISOString(),
                        metadata: {
                            appVersion: this.APP_VERSION,
                            catalogVersion: this.CATALOG_VERSION,
                            quoteVersion: this.QUOTE_VERSION
                        },
                        // Каталоги всех регионов
                        catalogs: {},
                        // Сметы из БД (через API)
                        estimates: [],
                        // Настройки
                        settings: {
                            currentRegion: this.currentRegion,
                            regions: this.regions,
                            hiddenMarkup: this.state.hiddenMarkup,
                            partnerCommission: this.state.partnerCommission
                        }
                    };

                    // Экспорт каталогов всех регионов из localStorage
                    for (const region of this.regions) {
                        const templatesKey = `quoteCalc_templates_${region}`;
                        const categoriesKey = `quoteCalc_categories_${region}`;

                        const templates = JSON.parse(localStorage.getItem(templatesKey) || '[]');
                        const categories = JSON.parse(localStorage.getItem(categoriesKey) || '[]');

                        exportData.catalogs[region] = {
                            templates,
                            categories
                        };
                    }

                    // Получение всех смет из БД через API
                    try {
                        const response = await this.apiClient.request('/api/estimates', 'GET');
                        if (response.success && response.data) {
                            exportData.estimates = response.data.map(est => ({
                                id: est.id,
                                filename: est.filename,
                                data: est.data,
                                data_version: est.data_version,
                                created_at: est.created_at,
                                updated_at: est.updated_at
                            }));
                        }
                    } catch (apiError) {
                        console.warn('Не удалось загрузить сметы из БД:', apiError);
                        this.showNotification(_t('app.toast.export_warning_no_estimates', 'Warning: quotes were not exported'), false);
                    }

                    // Сохранение в файл
                    const jsonString = JSON.stringify(exportData, null, 2);
                    const filename = `quote_calculator_backup_${new Date().toISOString().split('T')[0]}.json`;

                    this.downloadJSON(jsonString, filename);
                    this.showNotification(`${_t('app.toast.export_done', 'Export complete')}: ${exportData.estimates.length} ${_t('app.unit.quotes', 'quotes')}, ${this.regions.length} ${_t('app.unit.regions', 'regions')}`);
                } catch (error) {
                    console.error('Ошибка экспорта:', error);
                    this.showNotification(`${_t('app.error.export', 'Failed to export data')}: ` + error.message, true);
                }
            }

            async handleImportAll(event) {
                const file = event.target.files[0];
                if (!file) return;

                // File size validation
                if (file.size > 50 * 1024 * 1024) { // 50MB max
                    this.showNotification(_t('app.error.file_too_large_50mb', 'File too large (max 50MB)'), true);
                    event.target.value = '';
                    return;
                }

                // Сохраняем контекст this для использования в reader.onload
                const self = this;

                const reader = new FileReader();
                reader.onload = async (e) => {
    try {
        const json = JSON.parse(e.target.result);

        if (!json.version) {
            throw new Error(_t('app.error.invalid_format_no_version', 'Invalid file format: version missing'));
        }

        // Поддержка ОБОИХ форматов:
        // Новый: { version, data: { estimates, catalogs, ... } }
        // Старый: { version, catalogs, regionData, ... }
        let data;
        if (json.data) {
            // Новый формат
            data = json.data;
        } else {
            // Старый формат - оборачиваем в data
            data = {
                estimates: json.estimates || [],
                catalogs: json.catalogs || json.regionData || [],
                settings: json.settings || null,
                backups: json.backups || []
            };
        }

        const estimates = data.estimates || [];
        const catalogs = data.catalogs || [];
        const settings = data.settings || null;
        const backups = data.backups || [];

        // DEBUG: Логируем структуру данных
        console.log('Parsed import data:', {
            estimates: estimates.length,
            catalogs: catalogs,
            catalogsType: typeof catalogs,
            catalogsIsArray: Array.isArray(catalogs),
            settings: settings,
            backups: backups.length
        });

        // Подсчёт каталогов
        const catalogsCount = typeof catalogs === 'object' && !Array.isArray(catalogs)
            ? Object.keys(catalogs).length
            : 0;

        const importOk = await confirmModal({
            title: _t('app.confirm.import_title', 'Import data'),
            message:
                `${_t('app.confirm.import_question', 'Import data?')}\n\n` +
                `${_t('app.unit.quotes_label', 'Quotes')}: ${estimates.length}\n` +
                `${_t('app.confirm.regions_catalogs', 'Regions / Catalogs')}: ${catalogsCount}\n` +
                `${_t('app.confirm.settings_label', 'Settings')}: ${settings ? _t('common.yes', 'Yes') : _t('common.no', 'No')}\n\n` +
                _t('app.confirm.import_data_warn', 'Current data will be updated!'),
            danger: true,
            confirmLabel: _t('btn.import', 'Import'),
            cancelLabel: _t('btn.cancel', 'Cancel')
        });
        if (!importOk) {
            event.target.value = "";
            return;
        }

        let importedEstimates = 0;
        let importedCatalogs = 0;

        // 1. ИМПОРТ КАТАЛОГОВ через API (по регионам)
        console.log('Starting catalogs import...');
        if (Array.isArray(catalogs)) {
            // Проверяем формат массива
            if (catalogs.length > 0 && catalogs[0].filename && catalogs[0].data) {
                // ✅ FIX: Новый формат экспорта с filename и data
                console.log(`Export format: importing ${catalogs.length} catalogs with filename and data structure`);

                // ✅ FIX: Группируем каталоги по региону и объединяем templates
                // Проблема: несколько файлов могут быть для одного региона (catalog.json, catalog copy.json, catalog_Ushuaia.json)
                // UPSERT перезаписывает, поэтому последний файл (часто с 0 templates) затирает предыдущие
                // Решение: объединяем templates из всех файлов одного региона
                const catalogsByRegion = {};

                for (const catalogItem of catalogs) {
                    const catalogData = catalogItem.data;
                    const region = catalogData.region || 'Unknown';
                    const templates = catalogData.templates || [];
                    const categories = catalogData.categories || [];

                    console.log(`Reading catalog file: ${catalogItem.filename} for region ${region} (${templates.length} templates)`);

                    // Инициализация региона если ещё не существует
                    if (!catalogsByRegion[region]) {
                        catalogsByRegion[region] = {
                            templates: [],
                            categories: [],
                            filesCount: 0
                        };
                    }

                    catalogsByRegion[region].filesCount++;

                    // Merge templates - дедупликация по ID
                    const existingTemplateIds = new Set(catalogsByRegion[region].templates.map(t => t.id));
                    for (const template of templates) {
                        if (!existingTemplateIds.has(template.id)) {
                            catalogsByRegion[region].templates.push(template);
                            existingTemplateIds.add(template.id);
                        }
                    }

                    // Merge categories - дедупликация по ID
                    const existingCategoryIds = new Set(catalogsByRegion[region].categories.map(c => c.id));
                    for (const category of categories) {
                        if (category.id && !existingCategoryIds.has(category.id)) {
                            catalogsByRegion[region].categories.push(category);
                            existingCategoryIds.add(category.id);
                        }
                    }
                }

                // Сохраняем по одному каталогу на регион с объединёнными templates
                for (const [region, data] of Object.entries(catalogsByRegion)) {
                    console.log(`Importing merged catalog for region: ${region} (${data.templates.length} templates from ${data.filesCount} files)`);

                    try {
                        const response = await self.apiClient.saveCatalog(region, {
                            templates: data.templates,
                            categories: data.categories,
                            region: region  // ✅ Добавляем region в data для сохранения в БД
                        }, 'organization');

                        if (response.success) {
                            console.log(`✓ Successfully imported catalog: ${region} (${data.templates.length} templates, ${data.categories.length} categories)`);
                            importedCatalogs++;
                        } else {
                            console.error(`✗ Failed to import catalog ${region}:`, response.error);
                        }
                    } catch (apiError) {
                        console.error(`✗ Exception importing catalog ${region}:`, apiError);
                    }
                }
            } else {
                // Legacy формат: массив templates без привязки к регионам
                console.log(`Legacy format: importing ${catalogs.length} catalog items to current region`);
                const currentRegion = self.currentRegion || 'Ushuaia';

                try {
                    // Сохраняем через API v1
                    const catalogData = {
                        templates: catalogs,
                        categories: [] // Legacy формат не имел categories
                    };
                    const response = await self.apiClient.saveCatalog(currentRegion, catalogData, 'organization');

                    if (response.success) {
                        console.log(`✓ Successfully imported catalog: ${currentRegion} (${catalogs.length} templates)`);
                        importedCatalogs = 1;
                    } else {
                        console.error(`✗ Failed to import catalog ${currentRegion}:`, response.error);
                    }
                } catch (apiError) {
                    console.error(`✗ Exception importing catalog ${currentRegion}:`, apiError);
                }
            }
        } else if (typeof catalogs === 'object' && catalogs !== null) {
            // Новый формат: { "Atacama": { templates: [], categories: [] }, ... }
            for (const [region, regionData] of Object.entries(catalogs)) {
                console.log(`Importing catalog for region: ${region}`, regionData);

                try {
                    // Сохраняем через API v1
                    const catalogData = {
                        templates: regionData.templates || [],
                        categories: regionData.categories || []
                    };
                    const response = await self.apiClient.saveCatalog(region, catalogData, 'organization');

                    if (response.success) {
                        console.log(`✓ Successfully imported catalog: ${region} (${catalogData.templates.length} templates, ${catalogData.categories.length} categories)`);
                        importedCatalogs++;
                        // ✅ Каталог сохранён на сервер, localStorage НЕ используется
                    } else {
                        console.error(`✗ Failed to import catalog ${region}:`, response.error);
                    }
                } catch (apiError) {
                    console.error(`✗ Exception importing catalog ${region}:`, apiError);
                }
            }
        } else {
            console.warn('Catalogs format not recognized:', catalogs);
        }

        // 2. ИМПОРТ СМЕТ через API
        console.log(`Starting estimates import (${estimates.length} estimates)...`);
        for (const est of estimates) {
            try {
                // Генерируем ID если его нет (backward compatibility)
                const estimateId = est.id || est.data?.id || self.generateQuoteId();
                const estimateData = est.data || est;
                const estimateFilename = est.filename || estimateData.filename || `estimate_${estimateId}.json`;

                console.log(`Importing estimate: ${estimateFilename} (${estimateId})`);

                // Используем API для сохранения (self вместо this!)
                // ID-First: передаем (id, data) - порядок важен!
                const response = await self.apiClient.saveEstimate(estimateId, estimateData);

                if (response.success) {
                    console.log(`✓ Successfully imported: ${estimateFilename}`);
                    importedEstimates++;
                } else {
                    console.error(`✗ Failed to import ${estimateFilename}:`, response.error);
                }
            } catch (apiError) {
                console.error(`✗ Exception importing ${est.filename || 'unknown'}:`, apiError);
            }
        }
        console.log(`Imported ${importedEstimates}/${estimates.length} estimates`);

        // 3. ИМПОРТ НАСТРОЕК
        console.log('Starting settings import...', settings);
        if (settings) {
            if (settings.currentRegion) {
                self.currentRegion = settings.currentRegion;
                console.log(`Set currentRegion: ${settings.currentRegion}`);
            }
            if (settings.regions) {
                self.regions = settings.regions;
                console.log(`Set regions:`, settings.regions);
            }
            if (settings.hiddenMarkup !== undefined) {
                self.state.hiddenMarkup = settings.hiddenMarkup;
                console.log(`Set hiddenMarkup: ${settings.hiddenMarkup}`);
            }
            if (settings.partnerCommission !== undefined) {
                self.state.partnerCommission = settings.partnerCommission;
                console.log(`Set partnerCommission: ${settings.partnerCommission}`);
            }
        } else {
            console.log('No settings to import');
        }

        const reloadMsg =
            `${_t('app.toast.import_complete', 'Import complete!')}\n\n` +
            `${_t('app.unit.quotes_label', 'Quotes')}: ${importedEstimates}/${estimates.length}\n` +
            `${_t('app.unit.catalogs_label', 'Catalogs')}: ${importedCatalogs} ${_t('app.unit.regions', 'regions')}\n\n` +
            _t('app.confirm.reload_to_apply', 'Reload the page to apply changes?');

        event.target.value = '';

        // Предлагаем перезагрузку (с возможностью отказа для просмотра логов)
        const wantReload = await confirmModal({
            title: _t('app.toast.import_complete_title', 'Import complete'),
            message: reloadMsg,
            confirmLabel: _t('btn.reload', 'Reload'),
            cancelLabel: _t('btn.later', 'Later')
        });
        if (wantReload) {
            location.reload();
        } else {
            console.log('✅ Import completed. Page reload cancelled. You can reload manually when ready.');
        }

    } catch (err) {
        self.showNotification(`${_t('app.error.import', 'Import failed')}: ` + err.message, true);
        console.error(err);
        event.target.value = '';
    }
};

                reader.readAsText(file, 'UTF-8');
            }

            async handleExportDatabase() {
                try {
                    // Запрос на скачивание SQLite базы данных
                    const response = await fetch('/api/export/database');

                    if (!response.ok) {
                        throw new Error(_t('app.error.db_export_failed', 'Failed to export database'));
                    }

                    // Получаем blob
                    const blob = await response.blob();
                    const filename = `quotes_${new Date().toISOString().split('T')[0]}.db`;

                    // Создаём ссылку для скачивания
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);

                    this.showNotification(`${_t('app.toast.db_exported', 'Database exported')}: ` + filename);
                } catch (error) {
                    console.error('Ошибка экспорта БД:', error);
                    this.showNotification(`${_t('app.error.db_export', 'Failed to export database')}: ` + error.message, true);
                }
            }

            async handleImportDatabase(event) {
                const file = event.target.files[0];
                if (!file) return;

                // File size validation (максимум 100MB для базы данных)
                if (file.size > 100 * 1024 * 1024) {
                    this.showNotification(_t('app.error.db_file_too_large', 'Database file too large (max 100MB)'), true);
                    event.target.value = '';
                    return;
                }

                // Предупреждение о полной замене данных
                const okImportDb = await confirmModal({
                    title: _t('app.confirm.db_import_title', 'Warning'),
                    message:
                        '⚠️ ' + _t('app.confirm.db_import_warn_caps', 'WARNING!') + '\n\n' +
                        _t('app.confirm.db_import_replaces', 'Importing the database will FULLY REPLACE all current data:') + '\n' +
                        '• ' + _t('app.confirm.db_all_quotes', 'All quotes') + '\n' +
                        '• ' + _t('app.confirm.db_all_settings', 'All settings') + '\n' +
                        '• ' + _t('app.confirm.db_all_users', 'All users') + '\n\n' +
                        _t('app.confirm.db_irreversible', 'This action is IRREVERSIBLE!') + '\n\n' +
                        _t('app.confirm.db_continue', 'Continue with import?'),
                    danger: true,
                    confirmLabel: _t('btn.import', 'Import'),
                    cancelLabel: _t('btn.cancel', 'Cancel')
                });
                if (!okImportDb) {
                    event.target.value = '';
                    return;
                }

                try {
                    console.log('🔄 Starting database import...', {
                        filename: file.name,
                        size: file.size,
                        type: file.type
                    });

                    // Читаем файл как ArrayBuffer
                    const arrayBuffer = await file.arrayBuffer();

                    // Отправляем на сервер
                    const response = await fetch('/api/import/database', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/octet-stream'
                        },
                        body: arrayBuffer
                    });

                    const result = await response.json();

                    if (!response.ok || !result.success) {
                        throw new Error(result.error || _t('app.error.db_import_failed', 'Failed to import database'));
                    }

                    console.log('✅ Database import successful:', result);

                    this.showNotification(_t('app.toast.db_imported_restarting', 'Database imported! Server is restarting...'));

                    // Сервер перезапускается - ждем 5 секунд и проверяем доступность
                    setTimeout(async () => {
                        console.log('⏳ Waiting for server restart...');

                        // Пытаемся подключиться к серверу с интервалом 2 секунды
                        let attempts = 0;
                        const maxAttempts = 10;

                        const checkServer = async () => {
                            try {
                                attempts++;
                                console.log(`🔄 Checking server... (attempt ${attempts}/${maxAttempts})`);

                                const healthCheck = await fetch('/health');
                                if (healthCheck.ok) {
                                    console.log('✅ Server is back online!');
                                    this.showNotification(_t('app.toast.server_restarted_reloading', 'Server restarted! Reloading page...'));
                                    setTimeout(() => location.reload(), 1000);
                                } else if (attempts < maxAttempts) {
                                    setTimeout(checkServer, 2000);
                                } else {
                                    this.showNotification(_t('app.toast.server_not_responding', 'Server is not responding. Refresh the page manually.'), true);
                                }
                            } catch (err) {
                                if (attempts < maxAttempts) {
                                    setTimeout(checkServer, 2000);
                                } else {
                                    this.showNotification(_t('app.toast.server_not_responding', 'Server is not responding. Refresh the page manually.'), true);
                                }
                            }
                        };

                        checkServer();
                    }, 3000);

                } catch (error) {
                    console.error('❌ Database import error:', error);
                    this.showNotification(`${_t('app.error.db_import', 'Failed to import database')}: ` + error.message, true);
                } finally {
                    event.target.value = '';
                }
            }

            loadCatalogCSV(event) {
                const file = event.target.files[0];
                if (!file) return;

                // FIXED: File size validation
                if (file.size > this.config.maxFileSize) {
                    this.showNotification(`${_t('app.error.file_too_large', 'File too large')} (${(file.size/1024/1024).toFixed(1)}MB). ${_t('app.error.max_size_5mb', 'Maximum 5MB')}`, true);
                    event.target.value = '';
                    return;
                }

                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const csv = e.target.result;
                        const lines = csv.split('\n');
                        
                        // Skip header if exists
                        let startIndex = 0;
                        const firstLine = lines[0];
                        if (firstLine && (firstLine.includes('type,') || firstLine.includes('id,') || firstLine.includes('name,'))) {
                            startIndex = 1;
                        }
                        
                        const loadedCategories = [];
                        const loadedTemplates = [];
                        
                        lines
                            .slice(startIndex)
                            .filter(line => line.trim())
                            .forEach(line => {
                                const parts = this.parseCSVLine(line);
                                
                                // New format with description: type,id,name,category,description,contractor,icon,price
                                if (parts.length >= 8 && (parts[0] === 'category' || parts[0] === 'service')) {
                                    const [type, id, name, category, description, contractor, icon, price] = parts;
                                    
                                    if (type === 'category') {
                                        loadedCategories.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            icon: icon.replace(/"/g, '')
                                        });
                                    } else if (type === 'service') {
                                        loadedTemplates.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            category: category.replace(/"/g, ''),
                                            description: description.replace(/"/g, ''),
                                            contractor: contractor.replace(/"/g, ''),
                                            icon: icon.replace(/"/g, ''),
                                            price: parseFloat(price) || 0
                                        });
                                    }
                                }
                                // Old format compatibility without description: type,id,name,category,contractor,icon,price
                                else if (parts.length >= 7 && (parts[0] === 'category' || parts[0] === 'service')) {
                                    const [type, id, name, category, contractor, icon, price] = parts;
                                    
                                    if (type === 'category') {
                                        loadedCategories.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            icon: icon.replace(/"/g, '')
                                        });
                                    } else if (type === 'service') {
                                        loadedTemplates.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            category: category.replace(/"/g, ''),
                                            description: '', // Empty description for old format
                                            contractor: contractor.replace(/"/g, ''),
                                            icon: icon.replace(/"/g, ''),
                                            price: parseFloat(price) || 0
                                        });
                                    }
                                }
                                // Legacy formats without type field 
                                else if (parts.length >= 5) {
                                    const [id, name, category, contractor_or_icon, icon_or_price, price] = parts;
                                    
                                    if (parts.length >= 6) {
                                        // Format: id, name, category, contractor, icon, price
                                        loadedTemplates.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            category: category.replace(/"/g, ''),
                                            description: '',
                                            contractor: contractor_or_icon.replace(/"/g, ''),
                                            icon: icon_or_price.replace(/"/g, ''),
                                            price: parseFloat(price) || 0
                                        });
                                    } else {
                                        // Format: id, name, category, icon, price
                                        loadedTemplates.push({
                                            id: id.replace(/"/g, ''),
                                            name: name.replace(/"/g, ''),
                                            category: category.replace(/"/g, ''),
                                            description: '',
                                            contractor: '',
                                            icon: contractor_or_icon.replace(/"/g, ''),
                                            price: parseFloat(icon_or_price) || 0
                                        });
                                    }
                                }
                            });
                        
                        // Validate loaded data
                        const validTemplates = loadedTemplates.filter(t => t.name && t.price >= 0);
                        
                        if (validTemplates.length === 0 && loadedCategories.length === 0) {
                            this.showNotification(_t('app.error.file_no_valid_data', 'File does not contain valid data'), true);
                            return;
                        }

                        // Backup warning
                        if (this.templates.length > 0) {
                            const message = `${_t('app.confirm.csv_load_replace_prefix', 'Loading a new catalog will replace')} ${this.templates.length} ${_t('app.confirm.existing_services_suffix', 'existing services.')}\n\n${_t('app.confirm.backup_recommended', 'We recommend creating a backup.')}\n\n${_t('app.confirm.continue', 'Continue?')}`;
                            const okCsv = await confirmModal({
                                title: _t('app.confirm.catalog_load_title', 'Load catalog'),
                                message,
                                danger: true,
                                confirmLabel: _t('btn.continue', 'Continue'),
                                cancelLabel: _t('btn.cancel', 'Cancel')
                            });
                            if (!okCsv) {
                                event.target.value = '';
                                return;
                            }
                        }

                        // Apply loaded data
                        if (loadedCategories.length > 0) {
                            this.categories = loadedCategories;
                            this.saveCategories();
                            this.renderCategories();
                            this.renderCategoryOptions();
                        }
                        
                        if (validTemplates.length > 0) {
                            this.templates = validTemplates;
                            this.renderTemplates();
                        }
                        
                        this.saveToLocalStorage();
                        
                        // Result notification
                        let message = '';
                        if (loadedCategories.length > 0) {
                            message += `${loadedCategories.length} ${_t('app.unit.categories', 'categories')}`;
                        }
                        if (validTemplates.length > 0) {
                            if (message) message += ', ';
                            message += `${validTemplates.length} ${_t('app.unit.services', 'services')}`;
                        }
                        this.showNotification(`${_t('app.toast.loaded_prefix', 'Loaded')}: ${message}`);

                    } catch (error) {
                        this.showNotification(`${_t('app.error.catalog_load', 'Failed to load catalog')}: ` + error.message, true);
                        console.error('Catalog load error:', error);
                    }
                };
                reader.readAsText(file, 'UTF-8');
            }

            // COMPLETELY REWRITTEN CSV Functions for better compatibility
            saveQuoteCSV() {
                try {
                    const lines = ['type,field,value'];
                    
                    // Save state fields separately
                    Object.keys(this.state).forEach(key => {
                        if (key !== 'services' && key !== 'selectedServices') { // Skip services and selection, save separately
                            const value = this.escapeCSVField(JSON.stringify(this.state[key]));
                            lines.push(`meta,${key},${value}`);
                        }
                    });

                    // Save each service separately
                    this.state.services.forEach((service, index) => {
                        Object.keys(service).forEach(key => {
                            const value = this.escapeCSVField(JSON.stringify(service[key]));
                            lines.push(`service,${index}_${key},${value}`);
                        });
                    });

                    const csv = lines.join('\n');

                    // Generate smart filename
                    let filename = 'quote';
                    if (this.state.clientName) {
                        filename = this.state.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s]/g, '').replace(/\s+/g, '_');
                    }
                    if (this.state.tourStart && this.state.tourEnd) {
                        filename += `_${this.state.tourStart}_${this.state.tourEnd}`;
                    } else if (this.state.tourStart) {
                        filename += `_${this.state.tourStart}`;
                    }
                    filename += `_${Date.now()}.csv`;

                    this.downloadCSV(csv, filename);
                    this.showNotification(_t('app.toast.quote_saved_simple', 'Quote saved'));
                } catch (error) {
                    this.showNotification(`${_t('app.error.quote_save', 'Failed to save quote')}: ` + error.message, true);
                    console.error('Save error:', error);
                }
            }

            loadQuoteCSV(event) {
                const file = event.target.files[0];
                if (!file) return;

                // FIXED: File size validation
                if (file.size > this.config.maxFileSize) {
                    this.showNotification(`${_t('app.error.file_too_large', 'File too large')} (${(file.size/1024/1024).toFixed(1)}MB). ${_t('app.error.max_size_5mb', 'Maximum 5MB')}`, true);
                    event.target.value = '';
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const csv = e.target.result;
                        const lines = csv.split('\n');
                        
                        // Skip header if exists
                        let startIndex = 0;
                        if (lines[0] && (lines[0].includes('type,field') || lines[0].includes('type,data'))) {
                            startIndex = 1;
                        }
                        
                        let loadedState = { ...this.state, services: [], selectedServices: new Set() };
                        let loadedServices = [];
                        let serviceData = {};
                        let foundData = false;

                        lines.slice(startIndex).forEach(line => {
                            if (!line.trim()) return;
                            
                            const parts = this.parseCSVLine(line);
                            if (parts.length < 3) return;
                            
                            const [type, field, value] = parts;
                            
                            try {
                                const parsedValue = this.safeDecode(value.replace(/^"|"$/g, ''));
                                
                                if (type === 'meta') {
                                    if (field !== 'selectedServices') { // Skip selectedServices as it's transient
                                        loadedState[field] = parsedValue;
                                    }
                                    foundData = true;
                                } else if (type === 'service') {
                                    const [indexStr, serviceField] = field.split('_');
                                    const index = parseInt(indexStr);
                                    
                                    if (!serviceData[index]) {
                                        serviceData[index] = {};
                                    }
                                    serviceData[index][serviceField] = parsedValue;
                                    foundData = true;
                                } else if (type === 'services') {
                                    // Old format compatibility
                                    const parsed = this.safeDecode(value.replace(/^"|"$/g, ''));
                                    if (Array.isArray(parsed)) {
                                        loadedServices = parsed;
                                        foundData = true;
                                    }
                                }
                            } catch (e) {
                                console.warn('Warning: Could not parse field', field, ':', e);
                            }
                        });

                        // Convert serviceData object to array
                        if (Object.keys(serviceData).length > 0) {
                            loadedServices = Object.keys(serviceData)
                                .sort((a, b) => parseInt(a) - parseInt(b))
                                .map(key => serviceData[key])
                                .filter(service => service && service.name); // Validate service data
                        }

                        // Validate loaded data
                        if (!foundData) {
                            this.showNotification(_t('app.error.file_no_valid_data', 'File does not contain valid data'), true);
                            return;
                        }

                        if (!loadedServices.length) {
                            this.showNotification(_t('app.error.file_no_services', 'File does not contain any services'), true);
                            return;
                        }

                        // Apply loaded data
                        this.state = { ...loadedState, services: loadedServices, selectedServices: new Set() };

                        this.loadStateToUI();
                        this.renderServices();
                        this.updateCalculations();
                        this.showNotification(`${_t('app.toast.quote_loaded_count', 'Quote loaded')}: ${loadedServices.length} ${_t('app.unit.services', 'services')}`);
                    } catch (error) {
                        this.showNotification(`${_t('app.error.file_load', 'Failed to load file')}: ` + error.message, true);
                        console.error('Load error:', error);
                    }
                };
                reader.readAsText(file, 'UTF-8');
            }

            // Safe UTF-8 encoding for Russian text - SIMPLIFIED APPROACH
            safeEncode(str) {
                // Simple approach: just escape quotes and use direct JSON
                return JSON.stringify(str);
            }

            safeDecode(str) {
                try {
                    return JSON.parse(str);
                } catch (error) {
                    return str; // Return as-is if not JSON
                }
            }

            loadStateToUI() {
                const fields = {
                    'pax-count': this.state.paxCount || 27,
                    'tour-start': this.state.tourStart || '',
                    'tour-end': this.state.tourEnd || '',
                    'client-name': this.state.clientName || '',
                    'client-phone': this.state.clientPhone || '',
                    'client-email': this.state.clientEmail || '',
                    // ВАЖНО: используем ?? вместо || чтобы пустые строки загружались правильно
                    // ВАЖНО: используем quoteComments (НЕ quoteDescription) - это правильное имя из JSON
                    'quote-description': this.state.quoteComments ?? '',
                    'program-description': this.state.programDescription ?? '',
                    'booking-terms': this.state.bookingTerms ?? '',
                    'hidden-markup-slider': this.state.hiddenMarkup || 0,
                    'tax-rate': this.state.taxRate || 0,
                    'partner-commission-slider': this.state.partnerCommission || 0
                };

                Object.entries(fields).forEach(([id, value]) => {
                    const element = document.getElementById(id);
                    if (element) element.value = value;
                });

                // Форматируем текстовые поля для процентов с запятой
                const hiddenMarkupInput = document.getElementById('hidden-markup');
                if (hiddenMarkupInput) {
                    const value = this.state.hiddenMarkup || 0;
                    hiddenMarkupInput.value = value.toFixed(1).replace('.', ',');
                }

                const partnerCommissionInput = document.getElementById('partner-commission');
                if (partnerCommissionInput) {
                    const value = this.state.partnerCommission || 0;
                    partnerCommissionInput.value = value.toFixed(1).replace('.', ',');
                }

                // Sprint 19a: prepayment manual
                const prepaymentManualInput = document.getElementById('prepayment-manual');
                if (prepaymentManualInput) {
                    prepaymentManualInput.value = (this.state.prepaymentManual || 0).toString();
                }
                const prepaymentCurrencyLabel = document.getElementById('prepayment-currency-label');
                if (prepaymentCurrencyLabel) {
                    prepaymentCurrencyLabel.textContent = this.state.clientCurrency || 'USD';
                }

                // Sprint 19a: approval checkboxes
                const approvalPrepayment = document.getElementById('approval-prepayment');
                if (approvalPrepayment) approvalPrepayment.checked = !!this.state.prepaymentApprovedAt;
                const approvalFinal = document.getElementById('approval-final-payment');
                if (approvalFinal) approvalFinal.checked = !!this.state.finalPaymentApprovedAt;
                const fmtMeta = (ts, by) => ts ? `${by || ''} · ${new Date(ts * 1000).toLocaleDateString('ru-RU')}` : '';
                const ppMeta = document.getElementById('approval-prepayment-meta');
                if (ppMeta) ppMeta.textContent = fmtMeta(this.state.prepaymentApprovedAt, this.state.prepaymentApprovedBy);
                const fpMeta = document.getElementById('approval-final-payment-meta');
                if (fpMeta) fpMeta.textContent = fmtMeta(this.state.finalPaymentApprovedAt, this.state.finalPaymentApprovedBy);
            }

            downloadCSV(csv, filename) {
                try {
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    this.showNotification(`${_t('app.error.file_load', 'Failed to load file')}: ` + error.message, true);
                }
            }

            // 📦 prepareEstimateData(), saveToLocalStorage() — извлечены в
            //    public/js/app/autosave.js (Phase 5).

            migrateServicesRegions() {
                // Миграция данных: добавляем поле region к существующим услугам без региона
                let needsSave = false;

                if (this.state.services && this.state.services.length > 0) {
                    this.state.services = this.state.services.map(service => {
                        if (!service.region) {
                            needsSave = true;
                            return {
                                ...service,
                                region: this.currentRegion
                            };
                        }
                        return service;
                    });
                }

                if (this.state.optionalServices && this.state.optionalServices.length > 0) {
                    this.state.optionalServices = this.state.optionalServices.map(service => {
                        if (!service.region) {
                            needsSave = true;
                            return {
                                ...service,
                                region: this.currentRegion
                            };
                        }
                        return service;
                    });
                }

                // Сохраняем обновленные данные
                if (needsSave) {
                    console.log('Миграция: добавлены регионы к существующим услугам');
                    this.saveToLocalStorage();
                    this.renderServices();
                    if (this.state.optionalServices && this.state.optionalServices.length > 0) {
                        this.renderOptionalServices();
                    }
                }
            }

            async loadFromLocalStorage() {
                try {
                    // Загрузка каталога с проверкой версии
                    const savedTemplates = localStorage.getItem('quoteCalc_templates');
                    if (savedTemplates) {
                        const catalogData = JSON.parse(savedTemplates);

                        if (catalogData.version && catalogData.data) {
                            // Новый формат с версионированием
                            if (catalogData.version > this.CATALOG_VERSION) {
                                console.warn(`Catalog version mismatch: file ${catalogData.version}, app ${this.CATALOG_VERSION}`);
                            }
                            this.templates = catalogData.data;
                        } else {
                            // Старый формат без версионирования
                            this.templates = catalogData;
                        }
                        this.renderTemplates();
                    }

                    // Автосохранение будет загружено позже, после инициализации apiClient
                    // (см. QuoteCalc.loadAutosaveFromServer в Server Integration Script)

                    // ВАЖНО: НЕ загружаем state из localStorage!
                    // Теперь все сметы загружаются с сервера через loadAutosaveFromServer()
                    // Очищаем старые данные из localStorage (миграция)
                    localStorage.removeItem('quoteCalc_state');
                    localStorage.removeItem('quoteCalc_autosave');
                    localStorage.removeItem('quoteCalc_autosave_timestamp');

                    // Автозагрузка последней сметы
                    const lastQuote = localStorage.getItem('quoteCalc_lastQuote');
                    const lastQuoteFileName = localStorage.getItem('quoteCalc_lastQuoteFileName');

                    if (lastQuote && lastQuoteFileName && this.state.services.length === 0) {
                        try {
                            const quoteData = JSON.parse(lastQuote);

                            // Apply metadata (с миграцией старых полей)
                            if (quoteData.metadata) {
                                // Копируем metadata с переименованием quoteDescription → quoteComments
                                const metadata = { ...quoteData.metadata };
                                // Миграция: если есть старое поле quoteDescription, переименуем в quoteComments
                                if ('quoteDescription' in metadata && !('quoteComments' in metadata)) {
                                    metadata.quoteComments = metadata.quoteDescription;
                                    delete metadata.quoteDescription;
                                }
                                Object.assign(this.state, metadata);
                            }

                            // Apply services
                            if (quoteData.services) {
                                this.state.services = quoteData.services.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false,
                                    fullProfit: service.fullProfit || false,
                                    region: service.region || this.currentRegion // Миграция старых данных без региона
                                }));
                            }

                            // Apply optional services
                            if (quoteData.optionalServices) {
                                this.state.optionalServices = quoteData.optionalServices.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false,
                                    fullProfit: service.fullProfit || false,
                                    region: service.region || this.currentRegion // Миграция старых данных без региона
                                }));
                            }

                            // Apply hotels
                            if (quoteData.hotels) {
                                this.state.hotels = quoteData.hotels.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false,
                                    fullProfit: service.fullProfit || false,
                                    region: service.region || this.currentRegion
                                }));
                            } else {
                                this.state.hotels = [];
                            }

                            // Apply flights
                            if (quoteData.flights) {
                                this.state.flights = quoteData.flights.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false,
                                    fullProfit: service.fullProfit || false,
                                    region: service.region || this.currentRegion
                                }));
                            } else {
                                this.state.flights = [];
                            }

                            // Apply other services
                            if (quoteData.otherServices) {
                                this.state.otherServices = quoteData.otherServices.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false,
                                    fullProfit: service.fullProfit || false,
                                    region: service.region || this.currentRegion
                                }));
                            } else {
                                this.state.otherServices = [];
                            }

                            this.state.currentQuoteFile = lastQuoteFileName;
                            this.state.isQuoteSaved = true;
                            this.state.selectedServices = new Set();

                            this.loadStateToUI();
                            this.renderServices();
                            this.renderOptionalServices();
                            this.renderHotels();
                            this.renderFlights();
                            this.renderOtherServices();
                            this.updateCalculations();
                            this.updateQuoteStatusBar();
                            this.updateFiltersVisibility();

                            console.log(`Автоматически загружена последняя смета: ${lastQuoteFileName}`);
                        } catch (error) {
                            console.warn('Не удалось автоматически загрузить последнюю смету:', error);
                        }
                    }
                    // ===== VIEW MODE RESTORE - Восстановление режима отображения =====
                    // Загружаем сохраненный режим отображения (cards/table)
                    const savedViewMode = localStorage.getItem('quoteCalc_servicesViewMode');
                    if (savedViewMode === 'table' || savedViewMode === 'cards') {
                        this.state.servicesViewMode = savedViewMode;

                        // Обновляем визуальное состояние checkbox переключателя
                        const checkbox = document.getElementById('view-mode-checkbox');
                        if (checkbox) {
                            checkbox.checked = (savedViewMode === 'table');
                        }
                    }

                } catch (error) {
                    console.warn('Could not load from localStorage:', error);
                    this.state.selectedServices = new Set();
                    this.state.services = this.state.services || [];
                }
            }

            // Загрузка автосохраненной сметы
            loadAutosavedQuote(quoteData) {
                try {
                    console.log('Загрузка автосохраненной сметы...');

                    // Apply metadata (с миграцией старых полей)
                    if (quoteData.metadata) {
                        // Копируем metadata с переименованием quoteDescription → quoteComments
                        const metadata = { ...quoteData.metadata };
                        // Миграция: если есть старое поле quoteDescription, переименуем в quoteComments
                        if ('quoteDescription' in metadata && !('quoteComments' in metadata)) {
                            metadata.quoteComments = metadata.quoteDescription;
                            delete metadata.quoteDescription;
                        }
                        Object.assign(this.state, metadata);
                    }

                    // Apply services
                    if (quoteData.services) {
                        this.state.services = quoteData.services.map(service => ({
                            ...service,
                            excludeFromMarkup: service.excludeFromMarkup || false,
                            fullProfit: service.fullProfit || false,
                            region: service.region || this.currentRegion // Для старых автосохранений без региона
                        }));
                    }

                    // Apply optional services
                    if (quoteData.optionalServices) {
                        this.state.optionalServices = quoteData.optionalServices.map(service => ({
                            ...service,
                            excludeFromMarkup: service.excludeFromMarkup || false,
                            fullProfit: service.fullProfit || false,
                            region: service.region || this.currentRegion // Для старых автосохранений без региона
                        }));
                    }

                    // Флаг "несохраненной" сметы (т.к. это автосохранение)
                    this.state.currentQuoteFile = _t('app.label.autosave', 'Autosave');
                    this.state.isQuoteSaved = false;
                    this.state.selectedServices = new Set();

                    this.loadStateToUI();
                    this.renderServices();
                    this.renderOptionalServices();
                    this.updateCalculations();
                    this.updateQuoteStatusBar();
                    this.updateFiltersVisibility();

                    console.log('Автосохраненная смета успешно загружена');

                    // НЕ удаляем автосохранение - оно будет перезаписываться при изменениях
                    // или удалено при создании новой сметы/сохранении в файл
                } catch (error) {
                    console.warn('Ошибка при загрузке автосохранения:', error);
                    this.showNotification(_t('app.error.autosave_load', 'Failed to load autosave'), true);
                }
            }


            // ════════════════════════════════════════════════════════════════════
            // ▼ DOMAIN: RESET / PRINT
            //   📦 Извлечено в public/js/app/reset-print.js (Phase 5):
            //     - reset() / sendEmail() / print()
            //   print() — самый крупный метод (~785 строк HTML-template).
            // ════════════════════════════════════════════════════════════════════


            // Обновление статус-бара с информацией о файле и папке (новый header version)
            updateQuoteStatusBar() {
                const fileStatusHeader = document.getElementById('quote-file-status-header');
                const saveStatusHeader = document.getElementById('quote-save-status-header');

                if (!fileStatusHeader || !saveStatusHeader) return;

                // Имя файла
                const fileName = this.state.currentQuoteFile || _t('app.label.new_quote', 'New quote');
                const shortName = fileName.length > 30
                    ? '...' + fileName.slice(-27)
                    : fileName;

                const fileNameSpan = fileStatusHeader.querySelector('span');
                if (fileNameSpan) {
                    fileNameSpan.textContent = shortName;
                    fileNameSpan.title = fileName;
                }

                // Индикатор сохранения
                const savedTitle = _t('app.indicator.saved', 'Saved').replace(/"/g, '&quot;');
                const unsavedTitle = _t('app.indicator.unsaved_changes', 'Unsaved changes').replace(/"/g, '&quot;');
                if (this.state.isQuoteSaved) {
                    saveStatusHeader.innerHTML = `<span style="color: #10b981;" title="${savedTitle}">●</span>`;
                } else {
                    saveStatusHeader.innerHTML = `<span style="color: #f59e0b;" title="${unsavedTitle}">●</span>`;
                }

                // Инициализация Lucide иконок
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            }

            // Получить короткий путь (показать родительскую папку)
            getShortPath(folderName) {
                if (!folderName) return '';

                // Для File System Access API у нас обычно только имя папки
                // Показываем последние 30 символов если слишком длинное
                if (folderName.length > 30) {
                    return '...' + folderName.slice(-27);
                }
                return folderName;
            }

            // Отметить смету как несохраненную
            markQuoteAsUnsaved() {
                // Помечаем как несохраненную только если есть открытый файл
                if (this.state.currentQuoteFile && this.state.currentQuoteFile !== '') {
                    this.state.isQuoteSaved = false;
                    this.updateQuoteStatusBar();
                }
                // Если файл не открыт (новая смета) - ничего не делаем

                // Запускаем автосохранение (debounced - сработает через 2 секунды)
                this.debouncedAutosave();
            }

            // Методы сортировки услуг
            handleServicesSortChange() {
                const select = document.getElementById('services-sort-by');
                if (!select) return;

                this.state.servicesSortBy = select.value;
                this.applySortToServices();
                this.renderServices();
                this.showNotification(`${_t('app.toast.sort_applied', 'Sort')}: ${select.options[select.selectedIndex].text}`);
            }

            toggleSortDirection() {
                this.state.servicesSortDirection = this.state.servicesSortDirection === 'asc' ? 'desc' : 'asc';

                const btn = document.getElementById('sort-direction-btn');
                if (btn) {
                    btn.textContent = this.state.servicesSortDirection === 'asc' ? '↑' : '↓';
                }

                this.applySortToServices();
                this.renderServices();
            }

            resetServicesSort() {
                this.state.servicesSortBy = 'order';
                this.state.servicesSortDirection = 'asc';

                const select = document.getElementById('services-sort-by');
                if (select) select.value = 'order';

                const btn = document.getElementById('sort-direction-btn');
                if (btn) btn.textContent = '↑';

                this.renderServices();
                this.showNotification(_t('app.toast.sort_reset', 'Sort reset'));
            }

            // ===== VIEW MODE TOGGLE FUNCTION - Переключение режима отображения =====
            // Переключает между карточками (cards) и таблицей (table)
            toggleViewMode() {
                // Переключаем режим
                this.state.servicesViewMode = this.state.servicesViewMode === 'cards' ? 'table' : 'cards';

                // Обновляем визуальное состояние checkbox переключателя
                const checkbox = document.getElementById('view-mode-checkbox');
                if (checkbox) {
                    checkbox.checked = (this.state.servicesViewMode === 'table');
                }

                // Сохраняем выбор пользователя в localStorage
                localStorage.setItem('quoteCalc_servicesViewMode', this.state.servicesViewMode);

                // Перерисовываем список услуг в новом режиме
                this.renderServices();

                // Уведомление пользователю
                const modeText = this.state.servicesViewMode === 'table'
                    ? _t('app.view.table_mode', 'Table view')
                    : _t('app.view.cards_mode', 'Cards view');
                this.showNotification(modeText);
            }

            applySortToServices() {
                if (!this.state.services || this.state.services.length === 0) return;

                const sortBy = this.state.servicesSortBy;
                const direction = this.state.servicesSortDirection === 'asc' ? 1 : -1;

                this.state.services.sort((a, b) => {
                    let compareA, compareB;

                    switch (sortBy) {
                        case 'region':
                            // Сортировка по региону, внутри по дням
                            const regionA = (a.region || '').toLowerCase();
                            const regionB = (b.region || '').toLowerCase();

                            if (regionA !== regionB) {
                                if (regionA < regionB) return -1 * direction;
                                if (regionA > regionB) return 1 * direction;
                            }

                            // Если регионы одинаковые, сортируем по дням
                            const dayA = parseInt(a.day) || 0;
                            const dayB = parseInt(b.day) || 0;
                            if (dayA !== dayB) {
                                return (dayA - dayB) * direction;
                            }
                            return 0;

                        case 'day':
                            compareA = parseInt(a.day) || 0;
                            compareB = parseInt(b.day) || 0;
                            break;
                        case 'contractor':
                            compareA = (a.contractor || '').toLowerCase();
                            compareB = (b.contractor || '').toLowerCase();
                            break;
                        case 'order':
                        default:
                            // Используем исходный порядок (по индексу в массиве)
                            return 0;
                    }

                    if (compareA < compareB) return -1 * direction;
                    if (compareA > compareB) return 1 * direction;
                    return 0;
                });
            }

            // Обновить видимость панели фильтров
            updateFiltersVisibility() {
                const filtersPanel = document.getElementById('services-filters');
                if (!filtersPanel) return;

                // Показываем фильтры если есть хотя бы одна услуга
                filtersPanel.style.display = this.state.services.length > 0 ? 'block' : 'none';
            }
        }

        // Initialize
        // ВАЖНО: QuoteCalc теперь инициализируется ПОСЛЕ apiClient в Server Integration Script
        // const QuoteCalc = new ProfessionalQuoteCalculator();

        // Make QuoteCalc globally accessible for inline handlers
        // window.QuoteCalc = QuoteCalc;

        // 🔗 ESM-readiness: explicit window-global для класса.
        //   В classic-script режиме `class ProfessionalQuoteCalculator` уже global-hoisted.
        //   В ESM-режиме (когда файл импортируется как модуль) class остаётся module-scoped,
        //   и init.js не может его найти без явного присваивания на window.
        if (typeof window !== 'undefined') {
            window.ProfessionalQuoteCalculator = ProfessionalQuoteCalculator;
        }

        // ✅ FIX: Removed beforeunload catalog save - unreliable with fetch() during page unload
        // Catalog is already saved automatically when user makes changes to templates/categories
        // Modern browsers may cancel fetch() requests during beforeunload, causing "Failed to fetch" errors
        //
        // window.addEventListener('beforeunload', function() {
        //     try {
        //         if (window.QuoteCalc && window.QuoteCalc.currentRegion) {
        //             window.QuoteCalc.saveCatalogToRegion(window.QuoteCalc.currentRegion);
        //             console.log('Каталог сохранён перед закрытием страницы');
        //         }
        //     } catch (error) {
        //         console.error('Ошибка сохранения при закрытии:', error);
        //     }
        // });

        // 🔗 ESM-safe: ждём пока init.js создаст window.QuoteCalc, потом восстанавливаем папку.
        //   До исправления здесь была голая ссылка `QuoteCalc.state.saveFolder` —
        //   код выполнялся при load app.js, ДО того как init.js создаёт QuoteCalc, и тихо падал.
        //   В classic-mode проявлялось как «папка не восстанавливается»; в ESM было бы хард-fail.
        document.addEventListener('DOMContentLoaded', () => {
            const savedFolder = localStorage.getItem('quoteCalc_saveFolder');
            if (savedFolder && window.QuoteCalc?.state) {
                window.QuoteCalc.state.saveFolder = savedFolder;
                const btn = document.getElementById('select-folder-btn');
                if (btn) {
                    btn.title = `${_t('app.label.folder', 'Folder')}: ${savedFolder}`;
                    btn.style.background = '#16a34a';
                }
            }
        });

        // Performance monitoring
        if (window.performance) {
            window.addEventListener('load', () => {
                setTimeout(() => {
                    const timing = performance.timing;
                    const loadTime = timing.loadEventEnd - timing.navigationStart;
                    console.log(`Page loaded in ${loadTime}ms`);
                }, 0);
            });

            // Закрытие поиска при клике вне кнопки
            document.addEventListener('click', (e) => {
                const searchBtn = document.getElementById('search-expand-btn');
                if (searchBtn && searchBtn.classList.contains('expanded')) {
                    if (!searchBtn.contains(e.target)) {
                        searchBtn.classList.remove('expanded');
                        const searchInput = searchBtn.querySelector('.search-input');
                        if (searchInput && searchInput.value) {
                            searchInput.value = '';
                            // 🔗 ESM-safe: window.QuoteCalc вместо голого QuoteCalc
                            window.QuoteCalc?.filterTemplates('');
                        }
                    }
                }
            });

            // Инициализация Lucide иконок
            lucide.createIcons();
        }
